/* global globalThis */
/**
 * @fileoverview Add Disposition Modal Component
 * @module components/modals/AddEditOutcomeModal
 * @description A modal component for adding and managing disposition entries, including outreach information,
 * program details, and notes. Supports both provider and pharmacy outreach contexts.
 *
 * @requires react
 * @requires @mui/material
 * @requires @mui/icons-material/NoteOutlined
 * @requires @mui/icons-material/ExpandMore
 * @requires @mui/icons-material/Add
 * @requires @mui/icons-material/Close
 * @requires ../../api/disposition
 * @requires ../ConfirmationDialog
 * @requires ../../assets/notes.svg
 * @requires ../../assets/minus-circle.svg
 * @requires ../../assets/save.svg
 * @requires ../common/CommonSelect
 */

import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  TableCell,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import NoteIcon from "../../assets/notes.svg";
import NotesIcon from "../../assets/note-icon.svg";
import CommonSelect from "../common/CommonSelect";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import CloseIcon from "@mui/icons-material/Close";
import { addDisposition } from "../../api/disposition";
import ConfirmationDialog from "../ConfirmationDialog";
import SaveIcon from "../../assets/save.svg";

/** @constant {string[]} Outreach options specific to provider context */
const providerOutreachOptions = [
  "Dosing Optimization",
  "Provider Follow-Up",
  "Provider Education",
  "Provider Consultation",
];

/** @constant {string[]} Possible outcomes for pharmacy outreach */
const pharmacyOutreachOutcomes = [
  "Reached",
  "Not Reached - Left Message",
  "Not Reached - No Message",
];
/** @constant {string[]} Available pharmacy outreach methods */
const pharmacyOutreachMethods = ["Phone Call", "Email", "SMS"];
/** @constant {string[]} Outreach options specific to pharmacy context */
const pharmacyOutreachOptions = [
  "Pharmacy Consultation",
  "Confirm Prescription Received",
  "Request Prescription from Provider",
];
const pharmacyMemberOptions = ["Jane Cooper", "Bran Cooper", "Kristine Carl"];

/**
 * Determines which outreach options to show based on the current URL path
 * @returns {string[]} Array of outreach options based on context
 */
const getOutreachOptions = () => {
  const path = globalThis.location.pathname.toLowerCase();
  if (path.includes("pharmacyprofile") || path.includes("pharmacy-outreach")) {
    return pharmacyOutreachOptions;
  }
  return providerOutreachOptions;
};

/**
 * Determines which outreach methods to show based on the current URL path
 * @param {string[]} [dynamicMethods] - Dynamic methods from API
 * @returns {string[]} Array of outreach methods based on context
 */
const getOutreachMethods = (dynamicMethods) => {
  const path = globalThis.location.pathname.toLowerCase();
  if (
    path.includes("pharmacyprofile") ||
    path.includes("providerprofile") ||
    path.includes("pharmacy-outreach")
  ) {
    return pharmacyOutreachMethods;
  }

  if (dynamicMethods && dynamicMethods.length > 0) {
    return dynamicMethods;
  }
};
/**
 * Determines which outcome options to show based on context and status
 * @param {string} [status] - Current status that might affect available outcomes
 * @param {string[]} [dynamicOutcomes] - Dynamic outcomes from API
 * @returns {string[]} Array of outcome options based on context and status
 */
const getOutreachOutcomes = (status, dynamicOutcomes) => {
  const path = globalThis.location.pathname.toLowerCase();
  if (
    path.includes("pharmacyprofile") ||
    path.includes("providerprofile") ||
    path.includes("pharmacy-outreach")
  ) {
    return pharmacyOutreachOutcomes;
  }

  if (dynamicOutcomes && dynamicOutcomes.length > 0) {
    return dynamicOutcomes;
  }
};

/** @constant {Object} Default empty row template for new disposition entries */
const emptyRow = {
  program: "",
  medication: "",
  reasonForOutreach: "",
  status: "",
  method: "",
  outcome: "",
  outcomeReason: "",
  note: "",
  error: false,
};

const normalizeOutcome = (val) => (val || "").trim().toUpperCase();

const PENDING_OUTCOMES_UPPER = new Set([
  "",
  "N/A",
  "NA",
  "NO OUTCOME",
  "NO OUTCOME (NA)",
  "1ST OUTREACH COMPLETE – NO RESPONSE",
  "1ST OUTREACH COMPLETE - NO RESPONSE",
  "SCHEDULED CALLBACK",
  "ENROLLMENT IN PROGRESS",
]);

const isPendingExistingOutcome = (val) =>
  PENDING_OUTCOMES_UPPER.has(normalizeOutcome(val));

const getExistingOutcome = (opp) => opp?.recentActivity?.status || "";

const mapOpportunityToRow = (opp, title) => {
  const base = {
    ...emptyRow,
    program: opp.category || "",
    medication: { drug: opp.drug || "", drugNdc: opp.drugNdc || "" },
    status: (() => {
      if (opp.status === "Complete") return "Complete";
      if (opp.status === "ELIGIBLE") return "Eligible";
      if (opp.status === "DECLINED") return "Declined";
      return opp.status || "";
    })(),
    monitoringReason: opp.monitoringReason || "",
    note: opp?.recentActivity?.note || "",
    outcomeReason:
      opp.outcomeReason ||
      opp?.recentActivity?.reason ||
      opp?.recentActivity?.subCategory ||
      "",
  };
  if (title === "Add Disposition") {
    return base;
  } else {
    return {
      ...base,
      method: opp.methodDisplay || opp.method || "",
      outcome: opp.recentActivity?.status || "",
    };
  }
};

/**
 * Processes disposition opportunity data to initialize rows for the modal.
 * @param {Object|Array} dispositionOpportunity - Initial opportunity data.
 * @param {string} title - Modal title, used to determine initial row state.
 * @returns {{rows: Array, sourceOpps: Array, originalRowCount: number}} Initialized rows, source opportunities, and original row count.
 */
const getOpportunityArray = (dispositionOpportunity) => {
  if (!dispositionOpportunity) return [];
  if (Array.isArray(dispositionOpportunity)) {
    return dispositionOpportunity;
  }
  if (
    Array.isArray(dispositionOpportunity?.selected) &&
    dispositionOpportunity.selected.length > 0
  ) {
    return dispositionOpportunity.selected;
  }
  if (
    Array.isArray(dispositionOpportunity?.data) &&
    dispositionOpportunity.data.length > 0
  ) {
    return dispositionOpportunity.data;
  }
  return [];
};

const updateRowWithExisting = (row, idx, oppArray, title) => {
  const existing = getExistingOutcome(oppArray[idx]);
  const existingNote = oppArray[idx]?.recentActivity?.note || "";
  const existingOutcomeReason =
    oppArray[idx]?.outcomeReason ||
    oppArray[idx]?.recentActivity?.reason ||
    oppArray[idx]?.recentActivity?.subCategory ||
    "";

  if (title === "Add Disposition") {
    if (!isPendingExistingOutcome(existing) && !row.outcome) {
      return { ...row, outcome: existing };
    }
    if (existing && !row.outcome) {
      return { ...row, outcome: existing };
    }
    if (existingNote && !row.note) {
      return { ...row, note: existingNote };
    }
    if (existingOutcomeReason && !row.outcomeReason) {
      return { ...row, outcomeReason: existingOutcomeReason };
    }
  }
  return row;
};

const createRowsFromOpportunities = (oppArray, title) => {
  const filledRows = oppArray.map((opp) => mapOpportunityToRow(opp, title));
  return filledRows.map((row, idx) =>
    updateRowWithExisting(row, idx, oppArray, title),
  );
};

const createSingleRow = (opp, title) => {
  const baseRow = {
    ...emptyRow,
    program: opp.category || "",
    medication: opp.drug || "",
    outcome: opp.recentActivity?.status || "",
    monitoringReason: opp.monitoringReason || "",
    outcomeReason:
      opp.outcomeReason ||
      opp?.recentActivity?.reason ||
      opp?.recentActivity?.subCategory ||
      "",
  };

  if (title === "Add Disposition") {
    return {
      ...baseRow,
      note: opp?.recentActivity?.note || "",
    };
  }

  return {
    ...baseRow,
    status: opp.status || "",
    method: opp.methodDisplay || opp.method || "",
    note: opp?.recentActivity?.note || opp?.notes || opp?.note || "",
  };
};

const processDispositionOpportunity = (dispositionOpportunity, title) => {
  const oppArray = getOpportunityArray(dispositionOpportunity);

  if (oppArray.length > 0) {
    const rows = createRowsFromOpportunities(oppArray, title);
    return {
      rows,
      sourceOpps: oppArray,
      originalRowCount: rows.length,
    };
  }

  if (
    typeof dispositionOpportunity === "object" &&
    !Array.isArray(dispositionOpportunity) &&
    dispositionOpportunity !== null
  ) {
    const row = createSingleRow(dispositionOpportunity, title);
    return {
      rows: [row],
      sourceOpps: [dispositionOpportunity],
      originalRowCount: 1,
    };
  }

  return {
    rows: [{ ...emptyRow }],
    sourceOpps: [],
    originalRowCount: 0,
  };
};

/**
 * OutreachDispositionModal Component
 *
 * A comprehensive modal for managing disposition entries with support for multiple outreach types,
 * program selection, and detailed note-taking. Handles both provider and pharmacy outreach contexts.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls modal visibility
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onSave - Callback when changes are saved
 * @param {string} props.title - Modal title
 * @param {Object|Array} props.dispositionOpportunity - Initial opportunity data
 * @param {Array} props.programOptions - Available program options
 * @param {Object} props.programDrugMap - Mapping of programs to their associated drugs
 * @param {string|number} props.memberId - ID of the member
 * @param {Function} props.setSnackbar - Callback to show snackbar notifications
 * @param {boolean} [props.showMonitoringReason=false] - Whether to show monitoring reason field
 * @param {string} [props.memberBadgeValue] - Badge value to display member status
 * @returns {JSX.Element} The rendered modal component
 */
/**
 * OutcomeRow Component
 * Renders a single row in the outcome table.
 *
 * @param {Object} props - Component props
 * @returns {JSX.Element} The rendered row
 */
const OutcomeRowProgramCell = ({
  row,
  idx,
  title,
  originalRowCount,
  isOutreachContext,
  selectPopover,
  handleSelectClose,
  pharmacyMemberOptions,
  getAvailableProgramsForRow,
  handleSelectValue,
}) => (
  <TableCell
    sx={{
      ...(title === "Add Disposition" && idx < originalRowCount
        ? {
            backgroundColor: "#F5F5F5",
            color: "#403C6F",
            fontWeight: 600,
          }
        : undefined),
      cursor:
        title === "Add Disposition" && idx < originalRowCount
          ? "not-allowed"
          : "pointer",
      whiteSpace: "normal !important",
    }}
  >
    <span>
      {row.program || (
        <span style={{ color: "#403C6F", opacity: 0.6 }}>
          {isOutreachContext ? "Select member" : "Select program"}
        </span>
      )}
    </span>
    {selectPopover.idx === idx && selectPopover.field === "program" && (
      <CommonSelect
        id={`program-popover-${idx}`}
        open={Boolean(selectPopover.anchorEl)}
        anchorEl={selectPopover.anchorEl}
        onClose={handleSelectClose}
        filterButtonRef={{
          current: selectPopover.anchorEl,
        }}
        noteTypes={
          globalThis.location.pathname
            .toLowerCase()
            .includes("pharmacyprofile") ||
          globalThis.location.pathname
            .toLowerCase()
            .includes("providerprofile") ||
          globalThis.location.pathname
            .toLowerCase()
            .includes("pharmacy-outreach")
            ? pharmacyMemberOptions
            : getAvailableProgramsForRow(idx)
        }
        selectedTypes={row.program ? [row.program] : []}
        handleSelectAll={() =>
          handleSelectValue(
            (globalThis.location.pathname
              .toLowerCase()
              .includes("pharmacyprofile") ||
            globalThis.location.pathname
              .toLowerCase()
              .includes("providerprofile") ||
            globalThis.location.pathname
              .toLowerCase()
              .includes("pharmacy-outreach")
              ? pharmacyMemberOptions
              : getAvailableProgramsForRow(idx))[0],
          )
        }
        handleTypeToggle={(val) => handleSelectValue(val)}
      />
    )}
  </TableCell>
);

OutcomeRowProgramCell.propTypes = {
  row: PropTypes.object,
  idx: PropTypes.number,
  title: PropTypes.string,
  originalRowCount: PropTypes.number,
  isOutreachContext: PropTypes.bool,
  selectPopover: PropTypes.object,
  handleSelectClose: PropTypes.func,
  pharmacyMemberOptions: PropTypes.array,
  getAvailableProgramsForRow: PropTypes.func,
  handleSelectValue: PropTypes.func,
};

const OutcomeRowOutreachReasonCell = ({
  row,
  idx,
  selectPopover,
  handleSelectClose,
  getOutreachOptions,
  handleSelectValue,
  handleSelectOpen,
}) => (
  <TableCell>
    <Button
      variant="text"
      sx={{
        width: "100%",
        justifyContent: "space-between",
        color: row.reasonForOutreach ? "#403C6F" : "#9F9DB6",
        textTransform: "none",
        fontWeight: 600,
        display: "flex",
        alignItems: "center",
        px: 1,
      }}
      onClick={(e) => handleSelectOpen(e, idx, "reasonForOutreach")}
      endIcon={<ExpandMoreIcon sx={{ color: "#9F9DB6" }} />}
    >
      <span>
        {row.reasonForOutreach || (
          <span style={{ color: "#9F9DB6" }}>Select reason</span>
        )}
      </span>
    </Button>
    {selectPopover.idx === idx &&
      selectPopover.field === "reasonForOutreach" && (
        <CommonSelect
          id={`reason-popover-${idx}`}
          open={Boolean(selectPopover.anchorEl)}
          anchorEl={selectPopover.anchorEl}
          onClose={handleSelectClose}
          filterButtonRef={{
            current: selectPopover.anchorEl,
          }}
          noteTypes={getOutreachOptions()}
          selectedTypes={row.reasonForOutreach ? [row.reasonForOutreach] : []}
          handleSelectAll={() => handleSelectValue(getOutreachOptions()[0])}
          handleTypeToggle={(val) => handleSelectValue(val)}
        />
      )}
  </TableCell>
);

OutcomeRowOutreachReasonCell.propTypes = {
  row: PropTypes.object,
  idx: PropTypes.number,
  selectPopover: PropTypes.object,
  handleSelectClose: PropTypes.func,
  getOutreachOptions: PropTypes.func,
  handleSelectValue: PropTypes.func,
  handleSelectOpen: PropTypes.func,
};

const OutcomeRowMedicationCell = ({
  row,
  idx,
  title,
  originalRowCount,
  selectPopover,
  handleSelectClose,
  handleSelectOpen,
  handleSelectValue,
  getMedicationOptionsForRow,
  getMonitoringReasonForDrug,
  rows,
}) => (
  <TableCell
    sx={{
      ...(title === "Add Disposition" && idx < originalRowCount
        ? {
            backgroundColor: "#F5F5F5",
            color: "#403C6F",
            fontWeight: 600,
          }
        : undefined),
      cursor:
        title === "Add Disposition" && idx < originalRowCount
          ? "not-allowed"
          : "pointer",
    }}
  >
    <span>
      {(() => {
        if (typeof row.medication === "object") {
          return row.medication.drug;
        }
        if (
          typeof row.medication === "string" &&
          row.medication.includes("-")
        ) {
          return row.medication.split("-")[0];
        }
        return (
          row.medication || (
            <span style={{ color: "#9F9DB6" }}>Select medication</span>
          )
        );
      })()}
    </span>
    {selectPopover.idx === idx &&
      selectPopover.field === "medication" &&
      row.program && (
        <CommonSelect
          id={`medication-popover-${idx}`}
          open={Boolean(selectPopover.anchorEl)}
          anchorEl={selectPopover.anchorEl}
          onClose={handleSelectClose}
          filterButtonRef={{
            current: selectPopover.anchorEl,
          }}
          noteTypes={getMedicationOptionsForRow(rows[idx].program, idx).map(
            (drugObj) => drugObj.drug,
          )}
          selectedTypes={
            row.medication
              ? [
                  (() => {
                    if (typeof row.medication === "object") {
                      return row.medication.drug;
                    }
                    if (
                      typeof row.medication === "string" &&
                      row.medication.includes("-")
                    ) {
                      return row.medication.split("-")[0];
                    }
                    return row.medication;
                  })(),
                ].filter(Boolean)
              : []
          }
          handleSelectAll={() =>
            handleSelectValue(
              getMedicationOptionsForRow(rows[idx].program, idx)[0]?.drug,
            )
          }
          handleTypeToggle={(val) => {
            const drugObj = getMedicationOptionsForRow(
              rows[idx].program,
              idx,
            ).find((d) => d.drug === val);

            const monitoringReason = getMonitoringReasonForDrug(
              rows[idx].program,
              val,
            );

            const valueWithMonitoring = drugObj
              ? {
                  ...drugObj,
                  monitoringReason: monitoringReason,
                }
              : val;

            handleSelectValue(valueWithMonitoring);
          }}
        />
      )}
    <Typography
      variant="body2"
      component="span"
      sx={{
        display: "block",
        color: "#403C6F",
        fontSize: "12px",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      <span style={{ fontWeight: 300 }}>NDC:</span>{" "}
      <span style={{ fontWeight: 600 }}>{row.medication.drugNdc || "N/A"}</span>
    </Typography>
  </TableCell>
);

OutcomeRowMedicationCell.propTypes = {
  row: PropTypes.object,
  idx: PropTypes.number,
  title: PropTypes.string,
  originalRowCount: PropTypes.number,
  selectPopover: PropTypes.object,
  handleSelectClose: PropTypes.func,
  handleSelectOpen: PropTypes.func,
  handleSelectValue: PropTypes.func,
  getMedicationOptionsForRow: PropTypes.func,
  getMonitoringReasonForDrug: PropTypes.func,
  rows: PropTypes.array,
};

const OutcomeRowMonitoringCell = ({
  row,
  idx,
  memberBadgeValue,
  showMonitoringReason,
  handleChange,
}) => {
  if (memberBadgeValue !== "Member Monitoring" && !showMonitoringReason) {
    return null;
  }
  return (
    <TableCell>
      <Tooltip
        title={row.monitoringReason || ""}
        arrow
        placement="bottom-end"
        slotProps={{
          popper: {
            sx: {
              "& .MuiTooltip-tooltip": {
                backgroundColor: "#343435",
                color: "#FFFFFF",
              },
              "& .MuiTooltip-arrow": {
                color: "#343435",
                left: "50% !important",
                transform: "translateX(-50%) !important",
              },
            },
          },
        }}
      >
        <TextField
          variant="outlined"
          size="small"
          fullWidth
          value={row.monitoringReason || ""}
          onChange={(e) =>
            handleChange(idx, "monitoringReason", e.target.value)
          }
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
          disabled
          placeholder="Monitoring reason will auto-fill"
        />
      </Tooltip>
    </TableCell>
  );
};

OutcomeRowMonitoringCell.propTypes = {
  row: PropTypes.object,
  idx: PropTypes.number,
  memberBadgeValue: PropTypes.string,
  showMonitoringReason: PropTypes.bool,
  handleChange: PropTypes.func,
};

const OutcomeRowMethodCell = ({ idx, sourceOpps }) => {
  if (isPendingExistingOutcome(getExistingOutcome(sourceOpps[idx]))) {
    return null;
  }
  return (
    <TableCell
      sx={{
        backgroundColor: "#F5F5F5",
        color: "#403C6F",
        fontWeight: 600,
      }}
    >
      <span
        style={{
          color:
            sourceOpps[idx]?.methodDisplay || sourceOpps[idx]?.method
              ? "#403C6F"
              : "#9F9DB6",
        }}
      >
        {sourceOpps[idx]?.methodDisplay || sourceOpps[idx]?.method || "N/A"}
      </span>
    </TableCell>
  );
};

OutcomeRowMethodCell.propTypes = {
  idx: PropTypes.number,
  sourceOpps: PropTypes.array,
};

const OutcomeRowOutcomeCell = ({
  row,
  idx,
  title,
  originalRowCount,
  selectPopover,
  handleSelectOpen,
  handleSelectClose,
  handleSelectValue,
  dynamicOutcomes,
  dynamicOutcomeReasons,
  getDynamicOutcomeColor,
}) => {
  let outcomeDisplay;
  if (!row.outcome) {
    outcomeDisplay = <span style={{ color: "#9F9DB6" }}>Select outcome</span>;
  } else if (row.outcomeReason) {
    outcomeDisplay = (
      <span
        style={{
          textAlign: "start",
          display: "flex",
          alignItems: "center",
          width: "100%",
          whiteSpace: "nowrap",
          overflow: "hidden",
        }}
      >
        <span style={{ marginRight: "4px" }}>{row.outcome}</span>
        <Tooltip title={row.outcomeReason} arrow placement="bottom-start">
          <span
            style={{
              color: "#403C6F",
              fontSize: "12px",
              textAlign: "start",
              fontWeight: 400,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {`(${row.outcomeReason})`}
          </span>
        </Tooltip>
      </span>
    );
  } else {
    outcomeDisplay = (
      <span
        style={{
          textAlign: "start",
          display: "block",
          width: "100%",
        }}
      >
        <span>{row.outcome}</span>
      </span>
    );
  }
  return (
    <TableCell
      sx={{
        ...(title === "Add Disposition" && idx < originalRowCount
          ? { backgroundColor: "#fff" }
          : undefined),
      }}
    >
      <Button
        variant="text"
        sx={{
          width: "100%",
          justifyContent: "space-between",
          color: row.outcome ? getDynamicOutcomeColor(row.outcome) : "#9F9DB6",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "14px",
          display: "flex",
          alignItems: "center",
          px: 1,
          whiteSpace: "normal",
        }}
        onClick={(e) => handleSelectOpen(e, idx, "outcome")}
        endIcon={<ExpandMoreIcon sx={{ color: "#9F9DB6" }} />}
      >
        {outcomeDisplay}
      </Button>
      {selectPopover.idx === idx && selectPopover.field === "outcome" && (
        <CommonSelect
          id={`outcome-popover-${idx}`}
          open={Boolean(selectPopover.anchorEl)}
          anchorEl={selectPopover.anchorEl}
          onClose={handleSelectClose}
          filterButtonRef={{
            current: selectPopover.anchorEl,
          }}
          noteTypes={getOutreachOutcomes(row.status, dynamicOutcomes)}
          selectedTypes={row.outcome ? [row.outcome] : []}
          nestedOptions={dynamicOutcomeReasons}
          nestedSelectedValue={row.outcomeReason || ""}
          onNestedSelect={(parent, child) =>
            handleSelectValue({ outcome: parent, outcomeReason: child })
          }
          handleSelectAll={() => {
            const outcomes = getOutreachOutcomes(row.status, dynamicOutcomes);
            handleSelectValue(outcomes.length > 0 ? outcomes[0] : "");
          }}
          handleTypeToggle={(val) => handleSelectValue(val)}
        />
      )}
    </TableCell>
  );
};

OutcomeRowOutcomeCell.propTypes = {
  row: PropTypes.object,
  idx: PropTypes.number,
  title: PropTypes.string,
  originalRowCount: PropTypes.number,
  selectPopover: PropTypes.object,
  handleSelectOpen: PropTypes.func,
  handleSelectClose: PropTypes.func,
  handleSelectValue: PropTypes.func,
  dynamicOutcomes: PropTypes.array,
  dynamicOutcomeReasons: PropTypes.object,
  getDynamicOutcomeColor: PropTypes.func,
};

const OutcomeRowNotesCell = ({
  idx,
  title,
  originalRowCount,
  noteAnchor,
  noteRowIdx,
  handleNoteClick,
  handleNoteClose,
  noteValue,
  setNoteValue,
  noteError,
  setNoteError,
  handleNoteSave,
  initialNoteValue,
}) => (
  <TableCell
    sx={{
      ...(title === "Add Disposition" && idx < originalRowCount
        ? { backgroundColor: "#fff" }
        : undefined),
      textAlign: "center",
      position: "relative",
      zIndex: 2201,
      p: "0 !important", // override global padding
    }}
  >
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#fff",
        transition: "background 0.2s, box-shadow 0.2s",
        position: "relative",
        zIndex: 2001,
        m: 0,
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow:
            noteAnchor && noteRowIdx === idx
              ? "0 0 10px rgba(64,60,111,0.6)"
              : "none",
          transition: "all 0.3s ease-in-out",
        }}
      >
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleNoteClick(e, idx);
          }}
          size="small"
          sx={{
            padding: "4px",
            color: "#433ba0ff",
            "&:hover": {
              // backgroundColor: "transparent",
            },
          }}
        >
          <img
            src={NoteIcon}
            alt="Notes"
            style={{
              width: "20px",
              height: "20px",
            }}
          />
        </IconButton>
      </Box>
    </Box>
    <Popover
      open={Boolean(noteAnchor) && noteRowIdx === idx}
      anchorEl={noteAnchor}
      onClose={handleNoteClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      slotProps={{
        paper: {
          sx: {
            p: 2,
            mt: 0.4,
            width: 350,
            backgroundColor: "#F7F8FB",
            height: "auto",
            minHeight: "290px",
            zIndex: 1000,
            borderRadius: "12px",
            boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
          },
        },
      }}
    >
      <Box>
        <Box sx={{ position: "relative" }}>
          <TextField
            multiline
            minRows={2}
            value={noteValue}
            onPaste={(e) => {
              setNoteError("");
            }}
            onChange={(e) => {
              const val = e.target.value;
              setNoteValue(val);
              setNoteError("");
            }}
            fullWidth
            variant="outlined"
            size="small"
            sx={{
              backgroundColor: "#FFFFFF",
              width: "100%",
              minHeight: "200px",
              borderRadius: "8px",
              boxShadow: "0px 0px 2px 0px #00000040",
              overflow: "auto",
              "& .MuiOutlinedInput-root": {
                height: "100%",
                alignItems: "flex-start",
                padding: 0,
                "& textarea": {
                  padding: "16px",
                  boxSizing: "border-box",
                  overflow: "auto !important",
                  maxHeight: "calc(200px - 32px)",
                },
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
            }}
            placeholder="Enter note details"
          />
          <Box
            sx={{
              position: "absolute",
              bottom: "8px",
              right: "8px",
              fontSize: "12px",
              color: "#666666",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              padding: "2px 6px",
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          ></Box>
        </Box>
        {noteError && (
          <Typography
            sx={{
              color: "#d32f2f",
              fontSize: "12px",
              mt: 0.5,
              ml: 1,
            }}
          >
            {noteError}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mt: 1,
          gap: 1,
        }}
      >
        <Button
          onClick={handleNoteClose}
          variant="outlined"
          sx={{
            height: 38,
            borderRadius: "52px",
            border: "1px solid #9F9DB6",
            color: "#403C6F",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleNoteSave}
          variant="contained"
          disabled={noteValue === initialNoteValue}
          sx={{
            height: 38,
            borderRadius: "52px",
            backgroundColor:
              noteValue === initialNoteValue ? "#908f9bff" : "#3F3D56",
            cursor: noteValue === initialNoteValue ? "not-allowed" : "pointer",
            pointerEvents: noteValue === initialNoteValue ? "none" : "auto",
            "&:hover": {
              backgroundColor:
                noteValue === initialNoteValue ? "#908f9bff" : "#2F2C46",
            },
            "&.Mui-disabled": {
              backgroundColor: "#EFEFEF",
              color: "#999893",
            },
          }}
          title={noteValue === initialNoteValue ? "No changes to save" : ""}
        >
          Save
        </Button>
      </Box>
    </Popover>
  </TableCell>
);

OutcomeRowNotesCell.propTypes = {
  idx: PropTypes.number,
  title: PropTypes.string,
  originalRowCount: PropTypes.number,
  noteAnchor: PropTypes.object,
  noteRowIdx: PropTypes.number,
  handleNoteClick: PropTypes.func,
  handleNoteClose: PropTypes.func,
  noteValue: PropTypes.string,
  setNoteValue: PropTypes.func,
  noteError: PropTypes.string,
  setNoteError: PropTypes.func,
  handleNoteSave: PropTypes.func,
  initialNoteValue: PropTypes.string,
};

const MobileProgram = ({
  isOutreachContext,
  row,
  title,
  idx,
  originalRowCount,
  handleSelectOpen,
  selectPopover,
  handleSelectClose,
  pharmacyMemberOptions,
  getAvailableProgramsForRow,
  handleSelectValue,
}) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="caption"
      color="textSecondary"
      sx={{ mb: 0.5, display: "block", fontWeight: 600 }}
    >
      {isOutreachContext() ? "Member" : "Program"}
    </Typography>
    <Button
      variant="text"
      sx={{
        width: "100%",
        justifyContent: "space-between",
        color: row.program ? "#403C6F" : "#9F9DB6",
        textTransform: "none",
        fontWeight: 500,
        display: "flex",
        alignItems: "center",
        px: 1,
        border: "1px solid #E0E0E0",
        borderRadius: "8px",
        pointerEvents:
          title === "Manage Disposition" && idx < originalRowCount
            ? "none"
            : "auto",
        cursor:
          title === "Manage Disposition" && idx < originalRowCount
            ? "not-allowed"
            : "pointer",
        backgroundColor: "transparent",
        whiteSpace: "normal !important",
      }}
      onClick={(e) => handleSelectOpen(e, idx, "program")}
      endIcon={<ExpandMoreIcon sx={{ color: "#9F9DB6" }} />}
      disabled={title === "Manage Disposition" && idx < originalRowCount}
    >
      <span>
        {row.program || (
          <span style={{ color: "#9F9DB6" }}>
            {isOutreachContext() ? "Select member" : "Select program"}
          </span>
        )}
      </span>
    </Button>
    {selectPopover.idx === idx && selectPopover.field === "program" && (
      <CommonSelect
        id={`program-popover-mobile-${idx}`}
        open={Boolean(selectPopover.anchorEl)}
        anchorEl={selectPopover.anchorEl}
        onClose={handleSelectClose}
        filterButtonRef={{
          current: selectPopover.anchorEl,
        }}
        noteTypes={
          globalThis.location.pathname
            .toLowerCase()
            .includes("pharmacyprofile") ||
          globalThis.location.pathname
            .toLowerCase()
            .includes("providerprofile") ||
          globalThis.location.pathname
            .toLowerCase()
            .includes("pharmacy-outreach")
            ? pharmacyMemberOptions
            : getAvailableProgramsForRow(idx)
        }
        selectedTypes={row.program ? [row.program] : []}
        handleSelectAll={() =>
          handleSelectValue(
            (globalThis.location.pathname
              .toLowerCase()
              .includes("pharmacyprofile") ||
            globalThis.location.pathname
              .toLowerCase()
              .includes("providerprofile") ||
            globalThis.location.pathname
              .toLowerCase()
              .includes("pharmacy-outreach")
              ? pharmacyMemberOptions
              : getAvailableProgramsForRow(idx))[0],
          )
        }
        handleTypeToggle={(val) => handleSelectValue(val)}
      />
    )}
  </Box>
);

MobileProgram.propTypes = {
  isOutreachContext: PropTypes.func,
  row: PropTypes.object,
  title: PropTypes.string,
  idx: PropTypes.number,
  originalRowCount: PropTypes.number,
  handleSelectOpen: PropTypes.func,
  selectPopover: PropTypes.object,
  handleSelectClose: PropTypes.func,
  pharmacyMemberOptions: PropTypes.array,
  getAvailableProgramsForRow: PropTypes.func,
  handleSelectValue: PropTypes.func,
};

const MobileMedication = ({
  isOutreachContext,
  row,
  title,
  idx,
  originalRowCount,
  handleSelectOpen,
  selectPopover,
  handleSelectClose,
  getOutreachOptions,
  handleSelectValue,
  getMedicationOptionsForRow,
  rows,
  getMonitoringReasonForDrug,
}) => {
  const getMedicationLabel = (row) => {
    const isRowDisabled =
      (title === "Manage Disposition" && idx < originalRowCount) ||
      !row.program;

    let content;
    if (typeof row.medication === "object") {
      content = row.medication.drug;
    } else if (
      typeof row.medication === "string" &&
      row.medication.includes("-")
    ) {
      content = row.medication.split("-")[0];
    } else {
      content = row.medication || (
        <span
          style={{
            color: "#403C6F",
            opacity: 0.6,
          }}
        >
          Select medication
        </span>
      );
    }

    return (
      <Button
        variant="text"
        sx={{
          width: "100%",
          justifyContent: "space-between",
          color: row.medication ? "#403C6F" : "#9F9DB6",
          textTransform: "none",
          fontWeight: 500,
          display: "flex",
          alignItems: "center",
          px: 1,
          border: "1px solid #E0E0E0",
          borderRadius: "8px",
          pointerEvents: isRowDisabled ? "none" : "auto",
          cursor: isRowDisabled ? "not-allowed" : "pointer",
          backgroundColor: "transparent",
          opacity: row.program ? 1 : 0.5,
        }}
        onClick={(e) => {
          if (row.program) {
            handleSelectOpen(e, idx, "medication");
          }
        }}
        endIcon={<ExpandMoreIcon sx={{ color: "#9F9DB6" }} />}
        disabled={isRowDisabled}
      >
        <span>{content}</span>
      </Button>
    );
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ mb: 0.5, display: "block", fontWeight: 600 }}
      >
        {isOutreachContext() ? "Reason for Outreach" : "Medication"}
      </Typography>
      {isOutreachContext() ? (
        <>
          <Button
            variant="text"
            sx={{
              width: "100%",
              justifyContent: "space-between",
              color: row.reasonForOutreach ? "#403C6F" : "#9F9DB6",
              textTransform: "none",
              fontWeight: 500,
              display: "flex",
              alignItems: "center",
              px: 1,
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
            }}
            onClick={(e) => handleSelectOpen(e, idx, "reasonForOutreach")}
            endIcon={<ExpandMoreIcon sx={{ color: "#9F9DB6" }} />}
          >
            <span>
              {row.reasonForOutreach || (
                <span style={{ color: "#9F9DB6" }}>Select reason</span>
              )}
            </span>
          </Button>
          {selectPopover.idx === idx &&
            selectPopover.field === "reasonForOutreach" && (
              <CommonSelect
                id={`reason-popover-mobile-${idx}`}
                open={Boolean(selectPopover.anchorEl)}
                anchorEl={selectPopover.anchorEl}
                onClose={handleSelectClose}
                filterButtonRef={{
                  current: selectPopover.anchorEl,
                }}
                noteTypes={getOutreachOptions()}
                selectedTypes={
                  row.reasonForOutreach ? [row.reasonForOutreach] : []
                }
                handleSelectAll={() =>
                  handleSelectValue(getOutreachOptions()[0])
                }
                handleTypeToggle={(val) => handleSelectValue(val)}
              />
            )}
        </>
      ) : (
        <>
          {getMedicationLabel(row)}
          {selectPopover.idx === idx &&
            selectPopover.field === "medication" &&
            row.program && (
              <CommonSelect
                id={`medication-popover-mobile-${idx}`}
                open={Boolean(selectPopover.anchorEl)}
                anchorEl={selectPopover.anchorEl}
                onClose={handleSelectClose}
                filterButtonRef={{
                  current: selectPopover.anchorEl,
                }}
                noteTypes={getMedicationOptionsForRow(
                  rows[idx].program,
                  idx,
                ).map((drugObj) => drugObj.drug)}
                selectedTypes={
                  row.medication
                    ? [
                        (() => {
                          if (typeof row.medication === "object") {
                            return row.medication.drug;
                          }
                          if (
                            typeof row.medication === "string" &&
                            row.medication.includes("-")
                          ) {
                            return row.medication.split("-")[0];
                          }
                          return row.medication;
                        })(),
                      ].filter(Boolean)
                    : []
                }
                handleSelectAll={() =>
                  handleSelectValue(
                    getMedicationOptionsForRow(rows[idx].program, idx)[0]?.drug,
                  )
                }
                handleTypeToggle={(val) => {
                  const drugObj = getMedicationOptionsForRow(
                    rows[idx].program,
                    idx,
                  ).find((d) => d.drug === val);
                  const monitoringReason = getMonitoringReasonForDrug(
                    rows[idx].program,
                    val,
                  );
                  const valueWithMonitoring = drugObj
                    ? {
                        ...drugObj,
                        monitoringReason: monitoringReason,
                      }
                    : val;
                  handleSelectValue(valueWithMonitoring);
                }}
              />
            )}
        </>
      )}
    </Box>
  );
};

MobileMedication.propTypes = {
  isOutreachContext: PropTypes.func,
  row: PropTypes.object,
  title: PropTypes.string,
  idx: PropTypes.number,
  originalRowCount: PropTypes.number,
  handleSelectOpen: PropTypes.func,
  selectPopover: PropTypes.object,
  handleSelectClose: PropTypes.func,
  getOutreachOptions: PropTypes.func,
  handleSelectValue: PropTypes.func,
  getMedicationOptionsForRow: PropTypes.func,
  rows: PropTypes.array,
  getMonitoringReasonForDrug: PropTypes.func,
};

const MobileMonitoring = ({
  memberBadgeValue,
  showMonitoringReason,
  row,
  handleChange,
  idx,
}) =>
  (memberBadgeValue === "Member Monitoring" || showMonitoringReason) && (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ mb: 0.5, display: "block", fontWeight: 600 }}
      >
        Monitoring Reason
      </Typography>
      <Tooltip
        title={row.monitoringReason || ""}
        arrow
        placement="bottom-end"
        slotProps={{
          popper: {
            sx: {
              "& .MuiTooltip-tooltip": {
                backgroundColor: "#343435",
                color: "#FFFFFF",
              },
              "& .MuiTooltip-arrow": {
                color: "#343435",
                left: "50% !important",
                transform: "translateX(-50%) !important",
              },
            },
          },
        }}
      >
        <TextField
          variant="outlined"
          size="small"
          fullWidth
          value={row.monitoringReason || ""}
          onChange={(e) =>
            handleChange(idx, "monitoringReason", e.target.value)
          }
          slotProps={{
            input: {
              sx: {
                fontSize: "0.8rem",
                backgroundColor: "transparent",
                border: "1px solid #E0E0E0",
                borderRadius: "8px",
                padding: "8px",
                width: "100%",
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              },
            },
          }}
          disabled
          placeholder="Monitoring reason will auto-fill"
        />
      </Tooltip>
    </Box>
  );

MobileMonitoring.propTypes = {
  memberBadgeValue: PropTypes.string,
  showMonitoringReason: PropTypes.bool,
  row: PropTypes.object,
  handleChange: PropTypes.func,
  idx: PropTypes.number,
};

const MobileMethod = ({ sourceOpps, idx }) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="caption"
      color="textSecondary"
      sx={{ mb: 0.5, display: "block", fontWeight: 600 }}
    >
      Outreach Method
    </Typography>
    <Box
      sx={{
        width: "100%",
        border: "1px solid #E0E0E0",
        borderRadius: "8px",
        px: 1,
        py: 1,
        color:
          sourceOpps?.[idx]?.methodDisplay || sourceOpps?.[idx]?.method
            ? "#403C6F"
            : "#9F9DB6",
        fontWeight: 600,
        backgroundColor: "#F5F5F5",
        fontSize: "14px",
      }}
    >
      {sourceOpps?.[idx]?.methodDisplay || sourceOpps?.[idx]?.method || "N/A"}
    </Box>
  </Box>
);

MobileMethod.propTypes = {
  sourceOpps: PropTypes.array,
  idx: PropTypes.number,
};

const MobileOutcome = ({
  row,
  handleSelectOpen,
  idx,
  selectPopover,
  handleSelectClose,
  handleSelectValue,
  dynamicOutcomes,
  dynamicOutcomeReasons,
  getDynamicOutcomeColor,
}) => {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ mb: 0.5, display: "block", fontWeight: 600 }}
      >
        Outreach Outcome
      </Typography>
      <Button
        variant="text"
        sx={{
          width: "100%",
          justifyContent: "space-between",
          color: row.outcome ? getDynamicOutcomeColor(row.outcome) : "#9F9DB6",
          textTransform: "none",
          fontWeight: 500,
          display: "flex",
          alignItems: "center",
          px: 1,
          border: "1px solid #E0E0E0",
          borderRadius: "8px",
        }}
        onClick={(e) => handleSelectOpen(e, idx, "outcome")}
        endIcon={<ExpandMoreIcon sx={{ color: "#9F9DB6" }} />}
      >
        <span>
          {row.outcome ? (
            <>
              {row.outcome}
              {row.outcomeReason && (
                <span
                  style={{
                    color: "#403C6F",
                    fontSize: "12px",
                    marginLeft: 4,
                    fontWeight: 400,
                  }}
                >
                  {` (${row.outcomeReason})`}
                </span>
              )}
            </>
          ) : (
            <span style={{ color: "#9F9DB6" }}>Select outcome</span>
          )}
        </span>
      </Button>
      {selectPopover.idx === idx && selectPopover.field === "outcome" && (
        <CommonSelect
          id={`outcome-popover-mobile-${idx}`}
          open={Boolean(selectPopover.anchorEl)}
          anchorEl={selectPopover.anchorEl}
          onClose={handleSelectClose}
          filterButtonRef={{
            current: selectPopover.anchorEl,
          }}
          noteTypes={getOutreachOutcomes(row.status, dynamicOutcomes)}
          selectedTypes={row.outcome ? [row.outcome] : []}
          nestedOptions={dynamicOutcomeReasons}
          nestedSelectedValue={row.outcomeReason || ""}
          onNestedSelect={(parent, child) =>
            handleSelectValue({ outcome: parent, outcomeReason: child })
          }
          handleSelectAll={() => {
            const outcomes = getOutreachOutcomes(row.status, dynamicOutcomes);
            handleSelectValue(outcomes.length > 0 ? outcomes[0] : "");
          }}
          handleTypeToggle={(val) => handleSelectValue(val)}
        />
      )}
    </Box>
  );
};

MobileOutcome.propTypes = {
  row: PropTypes.object,
  handleSelectOpen: PropTypes.func,
  idx: PropTypes.number,
  selectPopover: PropTypes.object,
  handleSelectClose: PropTypes.func,
  handleSelectValue: PropTypes.func,
  dynamicOutcomes: PropTypes.array,
  dynamicOutcomeReasons: PropTypes.object,
  getDynamicOutcomeColor: PropTypes.func,
};

const MobileNotes = ({
  noteAnchor,
  noteRowIdx,
  idx,
  handleNoteClick,
  handleNoteClose,
  noteValue,
  setNoteValue,
  noteError,
  setNoteError,
  handleNoteSave,
  initialNoteValue,
}) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "flex-end",
      mt: 1,
    }}
  >
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: noteAnchor && noteRowIdx === idx ? "#fff" : "transparent",
        transition: "background 0.2s, box-shadow 0.2s",
        position: "relative",
        zIndex: 2001,
        m: 0,
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow:
            noteAnchor && noteRowIdx === idx
              ? "0 0 10px rgba(64,60,111,0.6)"
              : "none",
          transition: "all 0.3s ease-in-out",
        }}
      >
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleNoteClick(e, idx);
          }}
          size="small"
          sx={{
            padding: "4px",
            color: "#433ba0ff",
            "&:hover": {
              backgroundColor: "rgba(67, 59, 160, 0.08)",
            },
          }}
        >
          <img
            src={NotesIcon}
            alt="Notes"
            style={{
              width: "20px",
              height: "20px",
            }}
          />
        </IconButton>
      </Box>
    </Box>
    <Popover
      open={Boolean(noteAnchor) && noteRowIdx === idx}
      anchorEl={noteAnchor}
      onClose={handleNoteClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      slotProps={{
        paper: {
          sx: {
            p: 2,
            mt: 0.4,
            width: 350,
            backgroundColor: "#F7F8FB",
            height: "auto",
            minHeight: "290px",
            zIndex: 1000,
            borderRadius: "12px",
            boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
          },
        },
      }}
    >
      <Box>
        <Box sx={{ position: "relative" }}>
          <TextField
            multiline
            minRows={2}
            value={noteValue}
            onPaste={(e) => {
              setNoteError("");
            }}
            onChange={(e) => {
              const val = e.target.value;
              setNoteValue(val);
              setNoteError("");
            }}
            fullWidth
            variant="outlined"
            size="small"
            sx={{
              backgroundColor: "#FFFFFF",
              width: "100%",
              minHeight: "200px",
              borderRadius: "8px",
              boxShadow: "0px 0px 2px 0px #00000040",
              overflow: "auto",
              "& .MuiOutlinedInput-root": {
                height: "100%",
                alignItems: "flex-start",
                padding: 0,
                "& textarea": {
                  padding: "16px",
                  boxSizing: "border-box",
                  overflow: "auto !important",
                  maxHeight: "calc(200px - 32px)",
                },
                "& fieldset": {
                  border: "none",
                },
              },
            }}
            placeholder="Add a note..."
          />
          <Box
            sx={{
              position: "absolute",
              bottom: "8px",
              right: "8px",
              fontSize: "12px",
              color: "#6F6C90",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              padding: "2px 6px",
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            {(noteValue || "").length}
          </Box>
        </Box>
        {noteError && (
          <Typography
            sx={{
              color: "#d32f2f",
              fontSize: "12px",
              mt: 0.5,
              ml: 1,
            }}
          >
            {noteError}
          </Typography>
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 2,
            gap: 1,
          }}
        >
          <Button
            onClick={handleNoteClose}
            sx={{
              textTransform: "none",
              color: "#6F6C90",
              fontWeight: 600,
              fontSize: "14px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleNoteSave}
            disabled={noteValue === initialNoteValue}
            startIcon={<img src={SaveIcon} alt="Save" />}
            sx={{
              textTransform: "none",
              backgroundColor: "#403C6F",
              color: "#fff",
              fontWeight: 600,
              fontSize: "14px",
              padding: "6px 16px",
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: "#2F2C54",
              },
              "&.Mui-disabled": {
                backgroundColor: "#E0E0E0",
                color: "#9F9DB6",
              },
            }}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Popover>
  </Box>
);

MobileNotes.propTypes = {
  noteAnchor: PropTypes.object,
  noteRowIdx: PropTypes.number,
  idx: PropTypes.number,
  handleNoteClick: PropTypes.func,
  handleNoteClose: PropTypes.func,
  noteValue: PropTypes.string,
  setNoteValue: PropTypes.func,
  noteError: PropTypes.string,
  setNoteError: PropTypes.func,
  handleNoteSave: PropTypes.func,
  initialNoteValue: PropTypes.string,
};

const MobileOutcomeCard = ({
  sourceOpps,
  row,
  idx,
  handleRowClick,
  isOutreachContext,
  title,
  originalRowCount,
  handleSelectOpen,
  handleSelectClose,
  selectPopover,
  pharmacyMemberOptions,
  getAvailableProgramsForRow,
  handleSelectValue,
  getOutreachOptions,
  getMedicationOptionsForRow,
  rows,
  getMonitoringReasonForDrug,
  memberBadgeValue,
  showMonitoringReason,
  handleChange,
  noteAnchor,
  noteRowIdx,
  handleNoteClick,
  handleNoteClose,
  noteValue,
  setNoteValue,
  noteError,
  setNoteError,
  handleNoteSave,
  initialNoteValue,
  dynamicMethods,
  dynamicOutcomes,
  dynamicOutcomeReasons,
  getDynamicOutcomeColor,
}) => {
  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        borderRadius: "12px",
        border: row.error ? "1px solid #FFCDCD" : "1px solid #E0E0E0",
        bgcolor: row.error ? "#FFF0F0" : "#fff",
        position: "relative",
        boxShadow: "0px 2px 4px rgba(0,0,0,0.05)",
      }}
      onClick={() => handleRowClick(idx)}
    >
      <MobileProgram
        isOutreachContext={isOutreachContext}
        row={row}
        title={title}
        idx={idx}
        originalRowCount={originalRowCount}
        handleSelectOpen={handleSelectOpen}
        selectPopover={selectPopover}
        handleSelectClose={handleSelectClose}
        pharmacyMemberOptions={pharmacyMemberOptions}
        getAvailableProgramsForRow={getAvailableProgramsForRow}
        handleSelectValue={handleSelectValue}
      />

      <MobileMedication
        isOutreachContext={isOutreachContext}
        row={row}
        title={title}
        idx={idx}
        originalRowCount={originalRowCount}
        handleSelectOpen={handleSelectOpen}
        selectPopover={selectPopover}
        handleSelectClose={handleSelectClose}
        getOutreachOptions={getOutreachOptions}
        handleSelectValue={handleSelectValue}
        getMedicationOptionsForRow={getMedicationOptionsForRow}
        rows={rows}
        getMonitoringReasonForDrug={getMonitoringReasonForDrug}
      />

      <MobileMonitoring
        memberBadgeValue={memberBadgeValue}
        showMonitoringReason={showMonitoringReason}
        row={row}
        handleChange={handleChange}
        idx={idx}
      />

      <MobileMethod
        sourceOpps={sourceOpps}
        idx={idx}
        handleSelectOpen={handleSelectOpen}
        selectPopover={selectPopover}
        handleSelectClose={handleSelectClose}
        handleSelectValue={handleSelectValue}
        dynamicMethods={dynamicMethods}
      />

      <MobileOutcome
        row={row}
        handleSelectOpen={handleSelectOpen}
        idx={idx}
        selectPopover={selectPopover}
        handleSelectClose={handleSelectClose}
        handleSelectValue={handleSelectValue}
        dynamicOutcomes={dynamicOutcomes}
        dynamicOutcomeReasons={dynamicOutcomeReasons}
        getDynamicOutcomeColor={getDynamicOutcomeColor}
      />

      <MobileNotes
        noteAnchor={noteAnchor}
        noteRowIdx={noteRowIdx}
        idx={idx}
        handleNoteClick={handleNoteClick}
        handleNoteClose={handleNoteClose}
        noteValue={noteValue}
        setNoteValue={setNoteValue}
        noteError={noteError}
        setNoteError={setNoteError}
        handleNoteSave={handleNoteSave}
        initialNoteValue={initialNoteValue}
      />
    </Box>
  );
};

MobileOutcomeCard.propTypes = {
  sourceOpps: PropTypes.array,
  row: PropTypes.object,
  idx: PropTypes.number,
  handleRowClick: PropTypes.func,
  isOutreachContext: PropTypes.func,
  title: PropTypes.string,
  originalRowCount: PropTypes.number,
  handleSelectOpen: PropTypes.func,
  handleSelectClose: PropTypes.func,
  selectPopover: PropTypes.object,
  pharmacyMemberOptions: PropTypes.array,
  getAvailableProgramsForRow: PropTypes.func,
  handleSelectValue: PropTypes.func,
  getOutreachOptions: PropTypes.func,
  getMedicationOptionsForRow: PropTypes.func,
  rows: PropTypes.array,
  getMonitoringReasonForDrug: PropTypes.func,
  memberBadgeValue: PropTypes.string,
  showMonitoringReason: PropTypes.bool,
  handleChange: PropTypes.func,
  noteAnchor: PropTypes.object,
  noteRowIdx: PropTypes.number,
  handleNoteClick: PropTypes.func,
  handleNoteClose: PropTypes.func,
  noteValue: PropTypes.string,
  setNoteValue: PropTypes.func,
  noteError: PropTypes.string,
  setNoteError: PropTypes.func,
  handleNoteSave: PropTypes.func,
  initialNoteValue: PropTypes.string,
  dynamicMethods: PropTypes.array,
  dynamicOutcomes: PropTypes.array,
  dynamicOutcomeReasons: PropTypes.object,
  getDynamicOutcomeColor: PropTypes.func,
};

const OutcomeRow = ({
  row,
  idx,
  selectedRow,
  title,
  originalRowCount,
  isOutreachContext,
  handleRowClick,
  selectPopover,
  handleSelectClose,
  handleSelectOpen,
  handleSelectValue,
  getAvailableProgramsForRow,
  pharmacyMemberOptions,
  getOutreachOptions,
  getMedicationOptionsForRow,
  getMonitoringReasonForDrug,
  handleChange,
  sourceOpps,
  noteAnchor,
  noteRowIdx,
  handleNoteClick,
  handleNoteClose,
  noteValue,
  setNoteValue,
  noteError,
  setNoteError,
  handleNoteSave,
  initialNoteValue,
  showMonitoringReason,
  memberBadgeValue,
  rows,
  dynamicMethods,
  dynamicOutcomes,
  dynamicOutcomeReasons,
  getDynamicOutcomeColor,
}) => {
  return (
    <TableRow
      hover
      onClick={() => handleRowClick(idx)}
      sx={{
        background: (() => {
          if (row.error) return "#FFF0F0";
          if (selectedRow === idx) return "#F7F8FB";
          return "inherit";
        })(),
        cursor: "pointer",
      }}
    >
      <OutcomeRowProgramCell
        row={row}
        idx={idx}
        title={title}
        originalRowCount={originalRowCount}
        isOutreachContext={isOutreachContext}
        selectPopover={selectPopover}
        handleSelectClose={handleSelectClose}
        pharmacyMemberOptions={pharmacyMemberOptions}
        getAvailableProgramsForRow={getAvailableProgramsForRow}
        handleSelectValue={handleSelectValue}
      />

      {isOutreachContext ? (
        <OutcomeRowOutreachReasonCell
          row={row}
          idx={idx}
          selectPopover={selectPopover} // Fixed prop name to match definition
          handleSelectClose={handleSelectClose}
          getOutreachOptions={getOutreachOptions}
          handleSelectValue={handleSelectValue}
          handleSelectOpen={handleSelectOpen}
        />
      ) : (
        <OutcomeRowMedicationCell
          row={row}
          idx={idx}
          title={title}
          originalRowCount={originalRowCount}
          selectPopover={selectPopover}
          handleSelectClose={handleSelectClose}
          handleSelectOpen={handleSelectOpen}
          handleSelectValue={handleSelectValue}
          getMedicationOptionsForRow={getMedicationOptionsForRow}
          getMonitoringReasonForDrug={getMonitoringReasonForDrug}
          rows={rows}
        />
      )}

      <OutcomeRowMonitoringCell
        row={row}
        idx={idx}
        memberBadgeValue={memberBadgeValue}
        showMonitoringReason={showMonitoringReason}
        handleChange={handleChange}
      />

      <OutcomeRowMethodCell idx={idx} sourceOpps={sourceOpps} />

      <OutcomeRowOutcomeCell
        row={row}
        idx={idx}
        title={title}
        originalRowCount={originalRowCount}
        selectPopover={selectPopover}
        handleSelectOpen={handleSelectOpen}
        handleSelectClose={handleSelectClose}
        handleSelectValue={handleSelectValue}
        dynamicOutcomes={dynamicOutcomes}
        dynamicOutcomeReasons={dynamicOutcomeReasons}
        getDynamicOutcomeColor={getDynamicOutcomeColor}
      />

      <OutcomeRowNotesCell
        idx={idx}
        title={title}
        originalRowCount={originalRowCount}
        noteAnchor={noteAnchor}
        noteRowIdx={noteRowIdx}
        handleNoteClick={handleNoteClick}
        handleNoteClose={handleNoteClose}
        noteValue={noteValue}
        setNoteValue={setNoteValue}
        noteError={noteError}
        setNoteError={setNoteError}
        handleNoteSave={handleNoteSave}
        initialNoteValue={initialNoteValue}
      />
    </TableRow>
  );
};

OutcomeRow.propTypes = {
  row: PropTypes.object,
  idx: PropTypes.number,
  selectedRow: PropTypes.number,
  title: PropTypes.string,
  originalRowCount: PropTypes.number,
  isOutreachContext: PropTypes.bool,
  handleRowClick: PropTypes.func,
  selectPopover: PropTypes.object,
  handleSelectClose: PropTypes.func,
  handleSelectOpen: PropTypes.func,
  handleSelectValue: PropTypes.func,
  getAvailableProgramsForRow: PropTypes.func,
  pharmacyMemberOptions: PropTypes.array,
  getOutreachOptions: PropTypes.func,
  getMedicationOptionsForRow: PropTypes.func,
  getMonitoringReasonForDrug: PropTypes.func,
  handleChange: PropTypes.func,
  sourceOpps: PropTypes.array,
  noteAnchor: PropTypes.object,
  noteRowIdx: PropTypes.number,
  handleNoteClick: PropTypes.func,
  handleNoteClose: PropTypes.func,
  noteValue: PropTypes.string,
  setNoteValue: PropTypes.func,
  noteError: PropTypes.string,
  setNoteError: PropTypes.func,
  handleNoteSave: PropTypes.func,
  initialNoteValue: PropTypes.string,
  showMonitoringReason: PropTypes.bool,
  memberBadgeValue: PropTypes.string,
  rows: PropTypes.array,
  dynamicMethods: PropTypes.array,
  dynamicOutcomes: PropTypes.array,
  dynamicOutcomeReasons: PropTypes.object,
  getDynamicOutcomeColor: PropTypes.func,
};

const useOutcomeContext = (title) => {
  const isOutreachContext = useCallback(() => {
    const path = globalThis.location.pathname.toLowerCase();
    return (
      path.includes("providerprofile") ||
      path.includes("pharmacyprofile") ||
      title === "Provider Outreach" ||
      title === "Pharmacy Outreach"
    );
  }, [title]);

  const [headerOutreachMethod, setHeaderOutreachMethod] = useState("");
  const [headerMethodAnchor, setHeaderMethodAnchor] = useState(null);

  return {
    isOutreachContext,
    headerOutreachMethod,
    setHeaderOutreachMethod,
    headerMethodAnchor,
    setHeaderMethodAnchor,
  };
};

const useOutcomeNotes = () => {
  const [noteAnchor, setNoteAnchor] = useState(null);
  const [noteRowIdx, setNoteRowIdx] = useState(null);
  const [noteValue, setNoteValue] = useState("");
  const [initialNoteValue, setInitialNoteValue] = useState("");
  const [noteError, setNoteError] = useState("");

  const handleNoteClick = (event, idx, rows) => {
    if (noteAnchor && noteRowIdx === idx) {
      setNoteAnchor(null);
      setNoteRowIdx(null);
      setNoteValue("");
      setInitialNoteValue("");
      return;
    }
    setNoteAnchor(event.currentTarget);
    setNoteRowIdx(idx);
    const currentNote = rows[idx]?.note || "";
    setNoteValue(currentNote);
    setInitialNoteValue(currentNote);
  };

  const handleNoteClose = () => {
    setNoteAnchor(null);
    setNoteRowIdx(null);
    setNoteValue("");
    setNoteError("");
  };

  const handleNoteSave = (rows, setRows) => {
    setRows((currentRows) =>
      currentRows.map((row, i) =>
        i === noteRowIdx ? { ...row, note: noteValue } : row,
      ),
    );
    handleNoteClose();
  };

  return {
    noteAnchor,
    noteRowIdx,
    noteValue,
    setNoteValue,
    noteError,
    setNoteError,
    initialNoteValue,
    handleNoteClick,
    handleNoteClose,
    handleNoteSave,
  };
};

const useOutcomeHistory = () => {
  const [historyNoteAnchor, setHistoryNoteAnchor] = useState(null);
  const [activeHistoryNote, setActiveHistoryNote] = useState(null);

  const handleHistoryNoteClose = () => {
    setHistoryNoteAnchor(null);
    setActiveHistoryNote(null);
  };

  return {
    historyNoteAnchor,
    setHistoryNoteAnchor,
    activeHistoryNote,
    setActiveHistoryNote,
    handleHistoryNoteClose,
  };
};

const useOutcomeRows = ({
  open,
  dispositionOpportunity,
  title,
  programOptions,
  programDrugMap,
  memberBadgeValue,
  showMonitoringReason,
  headerOutreachMethod,
  isOutreachContext,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [rows, setRows] = useState([{ ...emptyRow }]);
  const [initialRows, setInitialRows] = useState([]);
  const [originalRowCount, setOriginalRowCount] = useState(0);
  const [sourceOpps, setSourceOpps] = useState([]);
  const prevOpenRef = React.useRef(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const [selectPopover, setSelectPopover] = useState({
    idx: null,
    field: null,
    anchorEl: null,
  });

  const getExistingOutcomeForIdx = useCallback(
    (idx) => getExistingOutcome(sourceOpps[idx]),
    [sourceOpps],
  );

  const indexedRows = React.useMemo(
    () => rows.map((row, idx) => ({ row, idx })),
    [rows],
  );

  const pendingEntries = React.useMemo(
    () =>
      indexedRows.filter(({ idx }) =>
        isPendingExistingOutcome(getExistingOutcomeForIdx(idx)),
      ),
    [indexedRows, getExistingOutcomeForIdx],
  );

  const finalEntries = React.useMemo(
    () =>
      indexedRows.filter(
        ({ idx }) => !isPendingExistingOutcome(getExistingOutcomeForIdx(idx)),
      ),
    [indexedRows, getExistingOutcomeForIdx],
  );

  React.useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;

    if (justOpened) {
      const {
        rows: newRows,
        sourceOpps: newSourceOpps,
        originalRowCount: newOriginalRowCount,
      } = processDispositionOpportunity(dispositionOpportunity, title);

      setRows(newRows);
      setInitialRows(newRows);
      setSourceOpps(newSourceOpps);
      setOriginalRowCount(newOriginalRowCount);
    }
  }, [open, dispositionOpportunity, title]);

  React.useEffect(() => {
    if (open) {
      setRows(initialRows.map((r) => ({ ...r })));
    }
  }, [open, initialRows]);

  const handleChange = (idx, field, value) => {
    setRows((rows) =>
      rows.map((row, i) => {
        if (i === idx) {
          const updatedRow = { ...row, [field]: value, error: false };
          if (field === "status") {
            updatedRow.outcome = "";
            updatedRow.outcomeReason = "";
          }
          if (field === "outcome") {
            updatedRow.outcomeReason = "";
          }
          return updatedRow;
        }
        return row;
      }),
    );
  };

  const handleRowClick = (idx) => setSelectedRow(idx);

  const handleSelectOpen = (event, idx, field) => {
    setSelectPopover({ idx, field, anchorEl: event.currentTarget });
  };
  const handleSelectClose = () => {
    setSelectPopover({ idx: null, field: null, anchorEl: null });
  };

  const getAvailableProgramsForRow = (currentIdx) => {
    const selectedPrograms = new Set(
      rows
        .filter((row, idx) => idx !== currentIdx && row.program)
        .map((row) => row.program),
    );

    return programOptions.filter((program) => !selectedPrograms.has(program));
  };

  const getMonitoringReasonForDrug = (program, drugName) => {
    if (!programDrugMap?.[program]) return "";

    if (
      programDrugMap[program].monitoringReasons &&
      Array.isArray(programDrugMap[program].monitoringReasons)
    ) {
      const drugIndex = programDrugMap[program].drugs.findIndex((drugStr) =>
        drugStr.startsWith(drugName + "-"),
      );

      if (
        drugIndex >= 0 &&
        programDrugMap[program].monitoringReasons[drugIndex]
      ) {
        return programDrugMap[program].monitoringReasons[drugIndex];
      }
    }
    return "";
  };

  const handleProgramChange = (value, idx) => {
    setRows((prev) =>
      prev.map((row, i) =>
        i === idx
          ? {
              ...row,
              program: value,
              medication: "",
              monitoringReason: "",
              status: "",
              error: false,
            }
          : row,
      ),
    );
  };

  const handleMedicationChange = (value, idx) => {
    const rowProgram = rows[idx].program;
    let drugName = "";
    let monitoringReason = "";

    if (typeof value === "object" && value.drug) {
      drugName = value.drug;
    } else if (typeof value === "string") {
      drugName = value;
    }

    if (drugName && rowProgram) {
      monitoringReason = getMonitoringReasonForDrug(rowProgram, drugName);
    }

    setRows((prev) =>
      prev.map((row, i) =>
        i === idx
          ? {
              ...row,
              medication: value,
              monitoringReason: monitoringReason,
              error: false,
            }
          : row,
      ),
    );
  };

  const handleSelectValue = (value) => {
    const { idx, field } = selectPopover;

    if (idx !== null && field) {
      if (field === "program") {
        handleProgramChange(value, idx);
      } else if (field === "medication") {
        handleMedicationChange(value, idx);
      } else if (
        field === "outcome" &&
        value &&
        typeof value === "object" &&
        (value.outcome || value.outcomeReason)
      ) {
        setRows((prev) =>
          prev.map((row, i) =>
            i === idx
              ? {
                  ...row,
                  outcome: value.outcome || row.outcome || "",
                  outcomeReason: value.outcomeReason || "",
                  error: false,
                }
              : row,
          ),
        );
      } else {
        handleChange(idx, field, value);
        if (field === "status") {
          // Status change might clear outcome as per existing logic in handleChange?
          // The previous logic had a side effect for "status" in handleChange, but
          // here we also saw a specific check for status in the original handleSelectValue
          // which did: ...(selectPopover.field === "status" ? { outcome: "" } : {}),
          // However, handleChange already handles status -> outcome reset (lines 1074-1076).
          // So directly calling handleChange is likely sufficient, BUT:
          // The original code passed 'error: false' explicitly in the map.
          // handleChange also sets error: false.
          // The only difference was the explicit `selectPopover.field === "status" ? { outcome: "" }`
          // which handleChange supposedly handles.
          // Let's verify `handleChange` logic again?
          // line 1074: if (field === "status") { updatedRow.outcome = ""; }
          // So it is safe.
        }
      }
    }
    handleSelectClose();
  };

  const getMedicationOptionsForRow = (rowProgram, currentIdx) => {
    if (!programDrugMap?.[rowProgram]) return [];

    const drugsArray =
      programDrugMap[rowProgram]?.drugs || programDrugMap[rowProgram];

    if (!Array.isArray(drugsArray)) return [];

    const selectedDrugKeys = new Set(
      rows
        .filter(
          (row, idx) =>
            idx !== currentIdx && row.program === rowProgram && row.medication,
        )
        .map((row) => row.medication),
    );

    return drugsArray
      .filter((drugStr) => !selectedDrugKeys.has(drugStr))
      .map((drugStr) => {
        const [drug, drugId, programId, ndc] = drugStr.split("-");
        return { drug, drugId, programId, ndc, value: drugStr, label: drug };
      });
  };

  // Determine which valid changed rows exist
  const getChangedIndices = useCallback(() => {
    const changed = [];
    const fields = [
      "program",
      "medication",
      "method",
      "outcome",
      "outcomeReason",
      "note",
      "monitoringReason",
    ];

    const normalizeFieldValue = (field, val) => {
      if (field === "medication") {
        if (val && typeof val === "object") {
          return val.drug || val.label || val.value || "";
        }
        if (typeof val === "string") {
          return val.includes("-") ? val.split("-")[0] : val;
        }
        return "";
      }
      return val || "";
    };

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i] || {};
      const initialRow = initialRows[i] || {};
      const isChanged = fields.some((field) => {
        const curr = normalizeFieldValue(field, row[field]);
        const prev = normalizeFieldValue(field, initialRow[field]);
        return curr !== prev;
      });
      if (isChanged) changed.push(i);
    }
    return changed;
  }, [rows, initialRows]);

  const checkForChanges = useCallback(() => {
    if (initialRows.length !== rows.length) return true;
    return getChangedIndices().length > 0;
  }, [initialRows, rows, getChangedIndices]);

  const checkRequiredFields = useCallback(() => {
    const changed = getChangedIndices();
    return changed.every((idx) => {
      const row = rows[idx] || {};
      const isPending = isPendingExistingOutcome(getExistingOutcomeForIdx(idx));
      const baseOK =
        !!row.program && !!row.medication && (isPending || !!row.outcome);
      const needsReason =
        row.outcome === "Not Enrolled" || row.outcome === "Member Declined";
      if (!baseOK) return false;
      if (needsReason && !row.outcomeReason) return false;
      if (isPending && row.outcome) {
        return !!row.method || !!headerOutreachMethod;
      }
      if (!isPending) {
        return !!headerOutreachMethod;
      }
      return true;
    });
  }, [rows, headerOutreachMethod, getChangedIndices, getExistingOutcomeForIdx]);

  return {
    rows,
    setRows,
    initialRows,
    setInitialRows,
    originalRowCount,
    sourceOpps,
    selectedRow,
    selectPopover,
    setSelectPopover,
    isMobile,
    getExistingOutcomeForIdx,
    indexedRows,
    pendingEntries,
    finalEntries,
    handleChange,
    handleRowClick,
    handleSelectOpen,
    handleSelectClose,
    handleSelectValue,
    getAvailableProgramsForRow,
    getMedicationOptionsForRow,
    getMonitoringReasonForDrug,
    getChangedIndices,
    checkForChanges,
    checkRequiredFields,
  };
};

const useOutcomeSubmission = ({
  rows,
  setRows,
  sourceOpps,
  setInitialRows,
  getChangedIndices,
  getExistingOutcomeForIdx,
  headerOutreachMethod,
  memberId,
  memberBadgeValue,
  showMonitoringReason,
  getMedicationOptionsForRow,
  onSave,
  handleClose,
  setSnackbar,
  checkForChanges,
  changedOutcomeIndices,
  checkRequiredFields,
  outreachData,
  textingConsentObtained,
}) => {
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const { outcomeMap, subReasonMap } = React.useMemo(() => {
    const oMap = {};
    const sMap = {};
    outreachData?.outcomes?.forEach((o) => {
      if (o.displayName) {
        oMap[o.displayName] = o.outcome;
        o.subReasons?.forEach((s) => {
          if (s.displayName) {
            sMap[`${o.displayName}|${s.displayName}`] = s.reason;
          }
        });
      }
    });
    return { outcomeMap: oMap, subReasonMap: sMap };
  }, [outreachData]);

  const methodMap = React.useMemo(() => {
    const map = {};
    outreachData?.outreachMethods?.forEach((m) => {
      if (m.displayName) {
        map[m.displayName] = m.methodName;
      }
    });
    return map;
  }, [outreachData]);

  useEffect(() => {
    setHasChanges(checkForChanges());
  }, [checkForChanges]);

  const getSubmitButtonTooltip = () => {
    if (!hasChanges) {
      return "No changes to save";
    }
    if (changedOutcomeIndices.length === 0) {
      return "Please select an Outreach Outcome";
    }
    if (!checkRequiredFields()) {
      return "Please fill required fields";
    }
    return "";
  };

  const handleSubmit = () => {
    let valid = true;
    const changedSet = new Set(getChangedIndices());
    const newRows = rows.map((row, idx) => {
      if (!changedSet.has(idx)) {
        return { ...row, error: false };
      }
      const isPending = isPendingExistingOutcome(getExistingOutcomeForIdx(idx));
      // Base validation
      const baseOK =
        !!row.program && !!row.medication && (isPending || !!row.outcome);
      const needsReason =
        row.outcome === "Not Enrolled" || row.outcome === "Member Declined";
      const reasonOK = !needsReason || !!row.outcomeReason;
      let methodOK = true;
      if (isPending) {
        methodOK = !row.outcome || !!row.method || !!headerOutreachMethod;
      } else {
        methodOK = !!headerOutreachMethod;
      }
      if (!(baseOK && reasonOK && methodOK)) {
        valid = false;
        return { ...row, error: true };
      }
      // If pending+outcome, auto-fill row.method from header if empty to keep UI/payload consistent
      if (isPending && row.outcome && !row.method && headerOutreachMethod) {
        return { ...row, method: headerOutreachMethod, error: false };
      }
      return { ...row, error: false };
    });
    setRows(newRows);
    if (!valid) return;
    setConfirmationOpen(true);
  };

  const handleConfirmDisposition = async () => {
    setIsSubmitting(true);
    setConfirmationOpen(false);

    // Only send changed rows with non-empty outcomes
    const changedIndices = getChangedIndices().filter(
      (idx) => !!rows[idx]?.outcome,
    );
    if (changedIndices.length === 0) {
      setIsSubmitting(false);
      return;
    }
    const payload = changedIndices.map((idx) => {
      const row = rows[idx];
      // Prefer the opportunity mapped to this row (sourceOpps)
      const opp = sourceOpps?.[idx] || sourceOpps?.[0] || {};
      let programId = "";
      let programDrugId = "";

      if (row && typeof row.medication === "object") {
        programId = row.medication.programId || "";
        programDrugId = row.medication.drugId || "";
      } else if (row && typeof row.medication === "string") {
        const drugName = row.medication.includes("-")
          ? row.medication.split("-")[0]
          : row.medication;
        const opt = getMedicationOptionsForRow(rows[idx].program, idx).find(
          (d) => d.drug === drugName,
        );
        if (opt) {
          programId = opt.programId || "";
          programDrugId = opt.drugId || "";
        }
      }

      // Fallback to opportunity-provided IDs if still missing
      if (!programId) {
        programId = opp.programId;
      }
      if (!programDrugId) {
        programDrugId = opp.drugId;
      }

      const outreachMethodToSend =
        methodMap[headerOutreachMethod] || headerOutreachMethod || "";

      const outreachOutcomeValue = outcomeMap[row.outcome] || row.outcome;
      const subCategoryValue =
        (row.outcomeReason &&
          subReasonMap[`${row.outcome}|${row.outcomeReason}`]) ||
        row.outcomeReason;

      return {
        programId,
        programDrugId,
        status:
          row.status === "Not Enrolled"
            ? "ELIGIBLE"
            : (row.status || "").toUpperCase().replaceAll(" ", "_"),
        outreachType: "MEMBER_OUTREACH",

        monitoringReason:
          memberBadgeValue === "Member Monitoring" || showMonitoringReason
            ? row.monitoringReason
            : "",
        outreachMethod: outreachMethodToSend,
        outreachOutcome: outreachOutcomeValue || "",
        ...(subCategoryValue && { subCategory: subCategoryValue }),
        note: row.note || "",
        textingConsentObtained: textingConsentObtained || false,
      };
    });

    try {
      const response = await addDisposition(memberId, payload);
      setIsSubmitting(true);
      if (response?.status === 200) {
        const changedRows = changedIndices.map((idx) => rows[idx]);
        onSave(changedRows);
        handleClose();
        // After successful save, refresh baseline to current rows
        setInitialRows(rows.map((r) => ({ ...r })));

        if (setSnackbar) {
          setSnackbar({
            open: true,
            message: "Disposition added successfully",
            severity: "success",
          });
        }
      }
      setIsSubmitting(false);
    } catch (err) {
      setIsSubmitting(false);

      if (err?.response?.data?.message?.includes("Invalid status transition")) {
        alert(err.response.data.message);
      }
    } finally {
      setIsSubmitting(false);
      setConfirmationOpen(false);
    }
  };

  return {
    confirmationOpen,
    setConfirmationOpen,
    isSubmitting,
    hasChanges,
    handleSubmit,
    handleConfirmDisposition,
    getSubmitButtonTooltip,
  };
};

const OutreachMethodHeader = ({
  headerOutreachMethod,
  headerMethodAnchor,
  setHeaderMethodAnchor,
  setHeaderOutreachMethod,
  dynamicMethods,
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
    <Typography sx={{ fontSize: "18px", fontWeight: 400, color: "#403C6F" }}>
      Outreach Method
    </Typography>
    <Button
      variant="text"
      sx={{
        justifyContent: "space-between",
        color: headerOutreachMethod ? "#403C6F" : "#9F9DB6",
        textTransform: "none",
        fontWeight: 600,
        fontSize: "14px",
        display: "flex",
        alignItems: "center",
        px: 1,
        minWidth: 160,
        border: "1px solid #E8E8E9",
        borderRadius: "28px",
      }}
      onClick={(e) => setHeaderMethodAnchor(e.currentTarget)}
      endIcon={<ExpandMoreIcon sx={{ color: "#9F9DB6" }} />}
    >
      {headerOutreachMethod || "Select method"}
    </Button>
    {headerMethodAnchor && (
      <CommonSelect
        id="header-method-popover"
        open={Boolean(headerMethodAnchor)}
        anchorEl={headerMethodAnchor}
        onClose={() => setHeaderMethodAnchor(null)}
        filterButtonRef={{
          current: headerMethodAnchor,
        }}
        noteTypes={getOutreachMethods(dynamicMethods)}
        selectedTypes={headerOutreachMethod ? [headerOutreachMethod] : []}
        handleSelectAll={() => {
          const methods = getOutreachMethods(dynamicMethods);
          setHeaderOutreachMethod(methods[0] || "");
          setHeaderMethodAnchor(null);
        }}
        handleTypeToggle={(val) => {
          setHeaderOutreachMethod(val);
          setHeaderMethodAnchor(null);
        }}
      />
    )}
  </Box>
);

OutreachMethodHeader.propTypes = {
  headerOutreachMethod: PropTypes.string,
  headerMethodAnchor: PropTypes.object,
  setHeaderMethodAnchor: PropTypes.func,
  setHeaderOutreachMethod: PropTypes.func,
  dynamicMethods: PropTypes.array,
};

const DesktopPendingOutcomesTable = ({
  pendingEntries,
  memberBadgeValue,
  showMonitoringReason,
  isOutreachContext,
  handleRowClick,
  selectedRow,
  title,
  originalRowCount,
  selectPopover,
  handleSelectClose,
  handleSelectOpen,
  handleSelectValue,
  getAvailableProgramsForRow,
  pharmacyMemberOptions,
  getOutreachOptions,
  getMedicationOptionsForRow,
  getMonitoringReasonForDrug,
  handleChange,
  sourceOpps,
  noteAnchor,
  noteRowIdx,
  handleNoteClick,
  handleNoteClose,
  noteValue,
  setNoteValue,
  noteError,
  setNoteError,
  handleNoteSave,
  initialNoteValue,
  rows,
  dynamicMethods,
  dynamicOutcomes,
  dynamicOutcomeReasons,
  getDynamicOutcomeColor,
}) => (
  <>
    <Typography sx={{ fontWeight: 700, color: "#10100F", fontSize: "16px" }}>
      Pending Outcomes
    </Typography>
    <TableContainer
      sx={{
        mt: 2,
        overflow: "auto",
        "&::-webkit-scrollbar": {
          width: "6px",
          height: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#888",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#555",
        },
      }}
    >
      <Table
        size="small"
        sx={{
          width: "100%",
          tableLayout: "fixed",
          "& .MuiTableCell-root": {
            color: "#403C6F",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            padding: { xs: "4px 6px", sm: "6px 8px" },
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
          },
          "& tbody .MuiTableCell-root": {
            borderBottom: "1px solid #E8E8E9",
            borderRight: "1px solid #fff",
            borderLeft: "1px solid #fff",
            borderTop: "1px solid #fff",
          },
          "& thead .MuiTableCell-root": {
            borderRight: "1px solid #fff",
            borderLeft: "1px solid #fff",
            position: "sticky",
            top: 0,
            zIndex: 3000,
            backgroundColor: "#CFCEDA",
          },
        }}
        stickyHeader
      >
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: "#CFCEDA",
              position: "sticky",
              top: 0,
              zIndex: 3000,
              height: "45px",
              "& .MuiTableCell-root": {
                verticalAlign: "middle",
              },
            }}
          >
            {isOutreachContext() ? (
              <TableCell sx={{ width: { xs: "20%", sm: "18%" } }}>
                Member
              </TableCell>
            ) : (
              <TableCell
                sx={{
                  width: { xs: "20%", sm: "10%" },
                  fontWeight: 700,
                  color: "#403C6F",
                }}
              >
                Program
              </TableCell>
            )}
            <TableCell
              sx={{
                width: { xs: "20%", sm: "15%" },
                fontWeight: 700,
                color: "#403C6F",
              }}
            >
              Medication
            </TableCell>
            <TableCell
              sx={{
                width: { xs: "15%", sm: "23%" },
                fontWeight: 700,
                color: "#403C6F",
              }}
            >
              Outreach Outcome
            </TableCell>
            <TableCell
              sx={{
                width: "5%",
                textAlign: "center",
                backgroundColor: "#CFCEDA",
              }}
            ></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pendingEntries.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={
                  memberBadgeValue === "Member Monitoring" ||
                  showMonitoringReason
                    ? 5
                    : 4
                }
                align="center"
                sx={{ color: "#9F9DB6" }}
              >
                No outreach records found
              </TableCell>
            </TableRow>
          ) : (
            pendingEntries.map(({ row, idx }) => (
              <OutcomeRow
                key={idx}
                row={row}
                idx={idx}
                selectedRow={selectedRow}
                title={title}
                originalRowCount={originalRowCount}
                isOutreachContext={isOutreachContext()}
                handleRowClick={handleRowClick}
                selectPopover={selectPopover}
                handleSelectClose={handleSelectClose}
                handleSelectOpen={handleSelectOpen}
                handleSelectValue={handleSelectValue}
                getAvailableProgramsForRow={getAvailableProgramsForRow}
                pharmacyMemberOptions={pharmacyMemberOptions}
                getOutreachOptions={getOutreachOptions}
                getMedicationOptionsForRow={getMedicationOptionsForRow}
                getMonitoringReasonForDrug={getMonitoringReasonForDrug}
                handleChange={handleChange}
                sourceOpps={sourceOpps}
                noteAnchor={noteAnchor}
                noteRowIdx={noteRowIdx}
                handleNoteClick={handleNoteClick}
                handleNoteClose={handleNoteClose}
                noteValue={noteValue}
                setNoteValue={setNoteValue}
                noteError={noteError}
                setNoteError={setNoteError}
                handleNoteSave={handleNoteSave}
                initialNoteValue={initialNoteValue}
                showMonitoringReason={showMonitoringReason}
                memberBadgeValue={memberBadgeValue}
                rows={rows}
                dynamicMethods={dynamicMethods}
                dynamicOutcomes={dynamicOutcomes}
                dynamicOutcomeReasons={dynamicOutcomeReasons}
                getDynamicOutcomeColor={getDynamicOutcomeColor}
              />
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </>
);

DesktopPendingOutcomesTable.propTypes = {
  pendingEntries: PropTypes.array,
  memberBadgeValue: PropTypes.string,
  showMonitoringReason: PropTypes.bool,
  isOutreachContext: PropTypes.func, // Changed to func based on usage isOutreachContext()
  handleRowClick: PropTypes.func,
  selectedRow: PropTypes.number,
  title: PropTypes.string,
  originalRowCount: PropTypes.number,
  selectPopover: PropTypes.object,
  handleSelectClose: PropTypes.func,
  handleSelectOpen: PropTypes.func,
  handleSelectValue: PropTypes.func,
  getAvailableProgramsForRow: PropTypes.func,
  pharmacyMemberOptions: PropTypes.array,
  getOutreachOptions: PropTypes.func,
  getMedicationOptionsForRow: PropTypes.func,
  getMonitoringReasonForDrug: PropTypes.func,
  handleChange: PropTypes.func,
  sourceOpps: PropTypes.array,
  noteAnchor: PropTypes.object,
  noteRowIdx: PropTypes.number,
  handleNoteClick: PropTypes.func,
  handleNoteClose: PropTypes.func,
  noteValue: PropTypes.string,
  setNoteValue: PropTypes.func,
  noteError: PropTypes.string,
  setNoteError: PropTypes.func,
  handleNoteSave: PropTypes.func,
  initialNoteValue: PropTypes.string,
  rows: PropTypes.array,
  dynamicMethods: PropTypes.array,
  dynamicOutcomes: PropTypes.array,
  dynamicOutcomeReasons: PropTypes.object,
  getDynamicOutcomeColor: PropTypes.func,
};

const DesktopFinalizedOutcomesTable = ({
  finalEntries,
  memberBadgeValue,
  showMonitoringReason,
  isOutreachContext,
  handleRowClick,
  selectedRow,
  title,
  originalRowCount,
  selectPopover,
  handleSelectClose,
  handleSelectOpen,
  handleSelectValue,
  getAvailableProgramsForRow,
  pharmacyMemberOptions,
  getOutreachOptions,
  getMedicationOptionsForRow,
  getMonitoringReasonForDrug,
  handleChange,
  sourceOpps,
  noteAnchor,
  noteRowIdx,
  handleNoteClick,
  handleNoteClose,
  noteValue,
  setNoteValue,
  noteError,
  setNoteError,
  handleNoteSave,
  initialNoteValue,
  rows,
  dynamicMethods,
  dynamicOutcomes,
  dynamicOutcomeReasons,
  getDynamicOutcomeColor,
}) => (
  <>
    <Typography
      sx={{
        mt: 3,
        fontWeight: 700,
        color: "#10100F",
        fontSize: "16px",
      }}
    >
      Finalized Outcomes
    </Typography>
    <TableContainer
      sx={{
        mt: 2,
        mb: 2,
        overflow: "auto",
        "&::-webkit-scrollbar": { width: "6px", height: "6px" },
        "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
        "&::-webkit-scrollbar-thumb": {
          background: "#888",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": { background: "#555" },
      }}
    >
      <Table
        size="small"
        sx={{
          width: "100%",
          tableLayout: "fixed",
          "& .MuiTableCell-root": {
            color: "#403C6F",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            padding: { xs: "4px 6px", sm: "6px 8px" },
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
          },
          "& tbody .MuiTableCell-root": {
            borderBottom: "1px solid #E8E8E9",
            borderRight: "1px solid #fff",
            borderLeft: "1px solid #fff",
            borderTop: "1px solid #fff",
          },
          "& thead .MuiTableCell-root": {
            borderRight: "1px solid #fff",
            borderLeft: "1px solid #fff",
            position: "sticky",
            top: 0,
            zIndex: 3000,
            backgroundColor: "#CFCEDA",
          },
        }}
        stickyHeader
      >
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: "#CFCEDA",
              position: "sticky",
              top: 0,
              zIndex: 3000,
              height: "45px",
              "& .MuiTableCell-root": { verticalAlign: "middle" },
            }}
          >
            {isOutreachContext() ? (
              <TableCell sx={{ width: { xs: "20%", sm: "18%" } }}>
                Member
              </TableCell>
            ) : (
              <TableCell
                sx={{
                  width: { xs: "20%", sm: "10%" },
                  fontWeight: 700,
                  color: "#403C6F",
                }}
              >
                Program
              </TableCell>
            )}
            <TableCell
              sx={{
                width: { xs: "20%", sm: "15%" },
                fontWeight: 700,
                color: "#403C6F",
              }}
            >
              Medication
            </TableCell>
            <TableCell
              sx={{
                width: { xs: "15%", sm: "13%" },
                fontWeight: 700,
                color: "#403C6F",
              }}
            >
              Outreach Method
            </TableCell>
            <TableCell
              sx={{
                width: { xs: "15%", sm: "28%" },
                fontWeight: 700,
                color: "#403C6F",
              }}
            >
              Outreach Outcome
            </TableCell>
            <TableCell
              sx={{
                width: "5%",
                textAlign: "center",
                backgroundColor: "#CFCEDA",
              }}
            ></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {finalEntries.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={
                  memberBadgeValue === "Member Monitoring" ||
                  showMonitoringReason
                    ? 5
                    : 4
                }
                align="center"
                sx={{ color: "#9F9DB6" }}
              >
                No outreach records found
              </TableCell>
            </TableRow>
          ) : (
            finalEntries.map(({ row, idx }) => (
              <OutcomeRow
                key={idx}
                row={row}
                idx={idx}
                selectedRow={selectedRow}
                title={title}
                originalRowCount={originalRowCount}
                isOutreachContext={isOutreachContext()}
                handleRowClick={handleRowClick}
                selectPopover={selectPopover}
                handleSelectClose={handleSelectClose}
                handleSelectOpen={handleSelectOpen}
                handleSelectValue={handleSelectValue}
                getAvailableProgramsForRow={getAvailableProgramsForRow}
                pharmacyMemberOptions={pharmacyMemberOptions}
                getOutreachOptions={getOutreachOptions}
                getMedicationOptionsForRow={getMedicationOptionsForRow}
                getMonitoringReasonForDrug={getMonitoringReasonForDrug}
                handleChange={handleChange}
                sourceOpps={sourceOpps}
                noteAnchor={noteAnchor}
                noteRowIdx={noteRowIdx}
                handleNoteClick={handleNoteClick}
                handleNoteClose={handleNoteClose}
                noteValue={noteValue}
                setNoteValue={setNoteValue}
                noteError={noteError}
                setNoteError={setNoteError}
                handleNoteSave={handleNoteSave}
                initialNoteValue={initialNoteValue}
                showMonitoringReason={showMonitoringReason}
                memberBadgeValue={memberBadgeValue}
                rows={rows}
                dynamicMethods={dynamicMethods}
                dynamicOutcomes={dynamicOutcomes}
                dynamicOutcomeReasons={dynamicOutcomeReasons}
                getDynamicOutcomeColor={getDynamicOutcomeColor}
              />
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </>
);

DesktopFinalizedOutcomesTable.propTypes = {
  finalEntries: PropTypes.array,
  memberBadgeValue: PropTypes.string,
  showMonitoringReason: PropTypes.bool,
  isOutreachContext: PropTypes.func, // Changed to func based on usage isOutreachContext()
  handleRowClick: PropTypes.func,
  selectedRow: PropTypes.number,
  title: PropTypes.string,
  originalRowCount: PropTypes.number,
  selectPopover: PropTypes.object,
  handleSelectClose: PropTypes.func,
  handleSelectOpen: PropTypes.func,
  handleSelectValue: PropTypes.func,
  getAvailableProgramsForRow: PropTypes.func,
  pharmacyMemberOptions: PropTypes.array,
  getOutreachOptions: PropTypes.func,
  getMedicationOptionsForRow: PropTypes.func,
  getMonitoringReasonForDrug: PropTypes.func,
  handleChange: PropTypes.func,
  sourceOpps: PropTypes.array,
  noteAnchor: PropTypes.object,
  noteRowIdx: PropTypes.number,
  handleNoteClick: PropTypes.func,
  handleNoteClose: PropTypes.func,
  noteValue: PropTypes.string,
  setNoteValue: PropTypes.func,
  noteError: PropTypes.string,
  setNoteError: PropTypes.func,
  handleNoteSave: PropTypes.func,
  initialNoteValue: PropTypes.string,
  rows: PropTypes.array,
  dynamicMethods: PropTypes.array,
  dynamicOutcomes: PropTypes.array,
  dynamicOutcomeReasons: PropTypes.object,
  getDynamicOutcomeColor: PropTypes.func,
};

const HistoryNotePopover = ({
  historyNoteAnchor,
  handleHistoryNoteClose,
  activeHistoryNote,
  isMobile,
  dynamicOutcomes,
  dynamicMethods,
  dynamicOutcomeReasons,
  getDynamicOutcomeColor,
}) => (
  <Popover
    open={Boolean(historyNoteAnchor)}
    anchorEl={historyNoteAnchor}
    onClose={handleHistoryNoteClose}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    slotProps={{
      paper: {
        sx: {
          p: 2,
          mt: 0.4,
          width: 350,
          backgroundColor: "#F7F8FB",
          height: "auto",
          minHeight: "290px",
          zIndex: 1000,
          borderRadius: "12px",
          boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
        },
      },
    }}
  >
    <Box sx={{ position: "relative" }}>
      <TextField
        multiline
        minRows={2}
        value={activeHistoryNote || ""}
        fullWidth
        variant="outlined"
        size="small"
        sx={{
          backgroundColor: "#FFFFFF",
          width: "100%",
          minHeight: "200px",
          borderRadius: "8px",
          boxShadow: "0px 0px 2px 0px #00000040",
          overflow: "auto",
          "& .MuiOutlinedInput-root": {
            height: "100%",
            alignItems: "flex-start",
            padding: 0,
            "& textarea": {
              padding: "16px",
              boxSizing: "border-box",
              overflow: "auto !important",
              maxHeight: "calc(200px - 32px)",
            },
          },
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
        }}
        slotProps={{
          input: {
            readOnly: true,
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "8px",
          right: "8px",
          fontSize: "12px",
          color: "#666666",
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          padding: "2px 6px",
          borderRadius: "4px",
          pointerEvents: "none",
        }}
      >
        {(activeHistoryNote || "").length}
      </Box>
    </Box>

    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        mt: 1,
        gap: 1,
      }}
    >
      <Button
        onClick={handleHistoryNoteClose}
        variant="outlined"
        sx={{
          height: 38,
          borderRadius: "52px",
          border: "1px solid #9F9DB6",
          color: "#403C6F",
        }}
      >
        Close
      </Button>
    </Box>
  </Popover>
);

HistoryNotePopover.propTypes = {
  historyNoteAnchor: PropTypes.object,
  handleHistoryNoteClose: PropTypes.func,
  activeHistoryNote: PropTypes.string,
  isMobile: PropTypes.bool,
  dynamicOutcomes: PropTypes.array,
  dynamicMethods: PropTypes.array,
  dynamicOutcomeReasons: PropTypes.object,
  getDynamicOutcomeColor: PropTypes.func,
};

const MobileOutcomesList = ({
  pendingEntries,
  finalEntries,
  sourceOpps,
  handleRowClick,
  isOutreachContext,
  title,
  originalRowCount,
  handleSelectOpen,
  handleSelectClose,
  selectPopover,
  pharmacyMemberOptions,
  getAvailableProgramsForRow,
  handleSelectValue,
  getOutreachOptions,
  getMedicationOptionsForRow,
  rows,
  getMonitoringReasonForDrug,
  memberBadgeValue,
  showMonitoringReason,
  handleChange,
  noteAnchor,
  noteRowIdx,
  handleNoteClick,
  handleNoteClose,
  noteValue,
  setNoteValue,
  noteError,
  setNoteError,
  handleNoteSave,
  initialNoteValue,
  dynamicMethods,
  dynamicOutcomes,
  dynamicOutcomeReasons,
  getDynamicOutcomeColor,
}) => (
  <Box
    sx={{
      mt: 2,
      maxHeight: "calc(90vh - 200px)",
      overflowY: "auto",
      px: 1,
    }}
  >
    <Typography
      sx={{
        mt: 1,
        fontWeight: 700,
        color: "#10100F",
        fontSize: "16px",
      }}
    >
      Pending Outcomes
    </Typography>
    {pendingEntries.map(({ row, idx }) => (
      <MobileOutcomeCard
        key={idx}
        sourceOpps={sourceOpps}
        row={row}
        idx={idx}
        handleRowClick={handleRowClick}
        isOutreachContext={isOutreachContext}
        title={title}
        originalRowCount={originalRowCount}
        handleSelectOpen={handleSelectOpen}
        handleSelectClose={handleSelectClose}
        selectPopover={selectPopover}
        pharmacyMemberOptions={pharmacyMemberOptions}
        getAvailableProgramsForRow={getAvailableProgramsForRow}
        handleSelectValue={handleSelectValue}
        getOutreachOptions={getOutreachOptions}
        getMedicationOptionsForRow={getMedicationOptionsForRow}
        rows={rows}
        getMonitoringReasonForDrug={getMonitoringReasonForDrug}
        memberBadgeValue={memberBadgeValue}
        showMonitoringReason={showMonitoringReason}
        handleChange={handleChange}
        noteAnchor={noteAnchor}
        noteRowIdx={noteRowIdx}
        handleNoteClick={handleNoteClick}
        handleNoteClose={handleNoteClose}
        noteValue={noteValue}
        setNoteValue={setNoteValue}
        noteError={noteError}
        setNoteError={setNoteError}
        handleNoteSave={handleNoteSave}
        initialNoteValue={initialNoteValue}
        dynamicMethods={dynamicMethods}
        dynamicOutcomes={dynamicOutcomes}
        dynamicOutcomeReasons={dynamicOutcomeReasons}
        getDynamicOutcomeColor={getDynamicOutcomeColor}
      />
    ))}
    {finalEntries.length > 0 && (
      <>
        <Typography
          sx={{
            mt: 3,
            fontWeight: 700,
            color: "#10100F",
            fontSize: "16px",
          }}
        >
          Finalized Outcomes
        </Typography>
        {finalEntries.map(({ row, idx }) => (
          <MobileOutcomeCard
            key={idx}
            sourceOpps={sourceOpps}
            row={row}
            idx={idx}
            handleRowClick={handleRowClick}
            isOutreachContext={isOutreachContext}
            title={title}
            originalRowCount={originalRowCount}
            handleSelectOpen={handleSelectOpen}
            handleSelectClose={handleSelectClose}
            selectPopover={selectPopover}
            pharmacyMemberOptions={pharmacyMemberOptions}
            getAvailableProgramsForRow={getAvailableProgramsForRow}
            handleSelectValue={handleSelectValue}
            getOutreachOptions={getOutreachOptions}
            getMedicationOptionsForRow={getMedicationOptionsForRow}
            rows={rows}
            getMonitoringReasonForDrug={getMonitoringReasonForDrug}
            memberBadgeValue={memberBadgeValue}
            showMonitoringReason={showMonitoringReason}
            handleChange={handleChange}
            noteAnchor={noteAnchor}
            noteRowIdx={noteRowIdx}
            handleNoteClick={handleNoteClick}
            handleNoteClose={handleNoteClose}
            noteValue={noteValue}
            setNoteValue={setNoteValue}
            noteError={noteError}
            setNoteError={setNoteError}
            handleNoteSave={handleNoteSave}
            initialNoteValue={initialNoteValue}
            dynamicMethods={dynamicMethods}
            dynamicOutcomes={dynamicOutcomes}
            dynamicOutcomeReasons={dynamicOutcomeReasons}
            getDynamicOutcomeColor={getDynamicOutcomeColor}
          />
        ))}
      </>
    )}
  </Box>
);

MobileOutcomesList.propTypes = {
  pendingEntries: PropTypes.array,
  finalEntries: PropTypes.array,
  sourceOpps: PropTypes.array,
  handleRowClick: PropTypes.func,
  isOutreachContext: PropTypes.func,
  title: PropTypes.string,
  originalRowCount: PropTypes.number,
  handleSelectOpen: PropTypes.func,
  handleSelectClose: PropTypes.func,
  selectPopover: PropTypes.object,
  pharmacyMemberOptions: PropTypes.array,
  getAvailableProgramsForRow: PropTypes.func,
  handleSelectValue: PropTypes.func,
  getOutreachOptions: PropTypes.func,
  getMedicationOptionsForRow: PropTypes.func,
  rows: PropTypes.array,
  getMonitoringReasonForDrug: PropTypes.func,
  memberBadgeValue: PropTypes.string,
  showMonitoringReason: PropTypes.bool,
  handleChange: PropTypes.func,
  noteAnchor: PropTypes.object,
  noteRowIdx: PropTypes.number,
  handleNoteClick: PropTypes.func,
  handleNoteClose: PropTypes.func,
  noteValue: PropTypes.string,
  setNoteValue: PropTypes.func,
  noteError: PropTypes.string,
  setNoteError: PropTypes.func,
  handleNoteSave: PropTypes.func,
  initialNoteValue: PropTypes.string,
  dynamicMethods: PropTypes.array,
  dynamicOutcomes: PropTypes.array,
  dynamicOutcomeReasons: PropTypes.object,
  getDynamicOutcomeColor: PropTypes.func,
};

const AddEditOutcomeModal = ({
  open,
  onClose,
  onSave,
  title,
  dispositionOpportunity,
  programOptions,
  programDrugMap,
  memberId,
  setSnackbar,
  showMonitoringReason = false,
  memberBadgeValue,
  canWrite = true,
  outreachData,
  textingConsentObtained: initialTextingConsent = false,
}) => {
  const dynamicOutcomes = React.useMemo(
    () => outreachData?.outcomes?.map((o) => o.displayName) || [],
    [outreachData],
  );

  const dynamicMethods = React.useMemo(
    () => outreachData?.outreachMethods?.map((m) => m.displayName) || [],
    [outreachData],
  );

  const dynamicOutcomeReasons = React.useMemo(() => {
    const reasons = {};
    outreachData.outcomes.forEach((o) => {
      if (o.subReasons && o.subReasons.length > 0) {
        reasons[o.displayName] = o.subReasons.map((s) => s.displayName);
      }
    });
    return reasons;
  }, [outreachData]);

  const dynamicOutcomeColors = React.useMemo(() => {
    if (!outreachData?.outcomes) return {};
    const colors = {};
    outreachData.outcomes.forEach((o) => {
      if (o.colourCode) {
        colors[o.displayName] = o.colourCode;
      }
    });
    return colors;
  }, [outreachData]);

  const getDynamicOutcomeColor = useCallback(
    (outcome) => {
      if (dynamicOutcomeColors[outcome]) {
        return dynamicOutcomeColors[outcome];
      }
    },
    [dynamicOutcomeColors],
  );
  const {
    isOutreachContext,
    headerOutreachMethod,
    setHeaderOutreachMethod,
    headerMethodAnchor,
    setHeaderMethodAnchor,
  } = useOutcomeContext(title);

  const [textingConsentObtained, setTextingConsentObtained] = useState(false);

  useEffect(() => {
    if (open) {
      setTextingConsentObtained(initialTextingConsent);
      if (dispositionOpportunity?.methodDisplay) {
        setHeaderOutreachMethod(dispositionOpportunity.methodDisplay);
      }
    }
  }, [
    open,
    initialTextingConsent,
    dispositionOpportunity,
    setHeaderOutreachMethod,
  ]);

  // Wrapper for close to handle header method reset
  const handleClose = () => {
    setHeaderOutreachMethod("");
    setTextingConsentObtained(false);
    if (typeof onClose === "function") {
      onClose();
    }
  };

  const {
    noteAnchor,
    noteRowIdx,
    noteValue,
    setNoteValue,
    noteError,
    setNoteError,
    handleNoteClick: handleNoteClickInternal,
    initialNoteValue,
    handleNoteClose,
    handleNoteSave: handleNoteSaveInternal,
  } = useOutcomeNotes();

  const {
    historyNoteAnchor,

    activeHistoryNote,

    handleHistoryNoteClose,
  } = useOutcomeHistory();

  const {
    rows,
    setRows,

    setInitialRows,
    originalRowCount,
    sourceOpps,
    selectedRow,
    selectPopover,

    isMobile,
    getExistingOutcomeForIdx,

    pendingEntries,
    finalEntries,
    handleChange,
    handleRowClick,
    handleSelectOpen,
    handleSelectClose,
    handleSelectValue,
    getAvailableProgramsForRow,
    getMedicationOptionsForRow,
    getMonitoringReasonForDrug,
    getChangedIndices,
    checkForChanges,
    checkRequiredFields,
  } = useOutcomeRows({
    open,
    dispositionOpportunity,
    title,
    programOptions,
    programDrugMap,
    memberBadgeValue,
    showMonitoringReason,
    headerOutreachMethod,
    isOutreachContext,
  });

  // Wrapper for Note Save to inject rows/setRows context
  const handleNoteSave = () => handleNoteSaveInternal(rows, setRows);
  // Wrapper for Note Click to inject current rows into useOutcomeNotes
  const handleNoteClick = (event, idx) =>
    handleNoteClickInternal(event, idx, rows);
  // Require at least one changed row with an Outreach outcome
  const changedOutcomeIndices = React.useMemo(
    () => getChangedIndices().filter((idx) => !!rows[idx]?.outcome),
    [getChangedIndices, rows],
  );

  const {
    confirmationOpen,
    setConfirmationOpen,
    isSubmitting,
    hasChanges,
    handleSubmit,
    handleConfirmDisposition,
    getSubmitButtonTooltip,
  } = useOutcomeSubmission({
    rows,
    setRows,
    sourceOpps,
    setInitialRows,
    getChangedIndices,
    getExistingOutcomeForIdx,
    headerOutreachMethod,
    memberId,
    memberBadgeValue,
    showMonitoringReason,
    getMedicationOptionsForRow,
    onSave,
    handleClose,
    setSnackbar,
    checkForChanges,
    changedOutcomeIndices,
    checkRequiredFields,
    outreachData,
    textingConsentObtained,
  });

  return (
    <Dialog
      open={open}
      maxWidth="xl"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            width: {
              xs: "95vw",
              sm: "90vw",
              md: "85vw",
              lg: "1000px",
              xl: "65vw",
            },
            borderRadius: "10px",
            backgroundColor: "#F7F8FB",
            position: "relative",
            maxHeight: "90vh",
            height: { xs: "90vh", md: "auto" },
            m: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      {" "}
      <Box
        sx={{
          position: "relative",
          p: 2,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          height: "100%",
          overflow: "hidden",
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            m: 0,
            width: "100%",
            height: "auto",
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-start",
            alignItems: "center",
            mb: 1,
            position: "relative",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "20px",
              lineHeight: "30px",
              color: "#000000",
              marginBottom: "6px",
              flex: 1,
              display: "flex",
              alignItems: "center",
            }}
          >
            Add/Edit Outcome
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 1,
              color: "#403C6F",
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {noteAnchor && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.2)",
              borderRadius: "16px",
              zIndex: 1,
              p: 0,
              m: 0,
              pointerEvents: "none",
            }}
          />
        )}
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            flex: 1,
            minHeight: "200px",
            maxHeight: "calc(90vh - 150px)",
            overflowY: "auto",
            borderRadius: "16px",
            border: "1px solid #E0E0E0",
            gap: "0px",
            m: 0,
            p: { xs: "6px 8px", sm: "6px 12px", md: "6px 18px" },
            backgroundColor: "#FFFFFF",
            position: "relative",
            zIndex: 0,
          }}
        >
          <OutreachMethodHeader
            headerOutreachMethod={headerOutreachMethod}
            headerMethodAnchor={headerMethodAnchor}
            setHeaderMethodAnchor={setHeaderMethodAnchor}
            setHeaderOutreachMethod={setHeaderOutreachMethod}
            dynamicMethods={dynamicMethods}
          />
          <Box sx={{ pt: "10px !important" }}>
            {isMobile ? (
              <MobileOutcomesList
                pendingEntries={pendingEntries}
                finalEntries={finalEntries}
                sourceOpps={sourceOpps}
                handleRowClick={handleRowClick}
                isOutreachContext={isOutreachContext}
                title={title}
                originalRowCount={originalRowCount}
                handleSelectOpen={handleSelectOpen}
                handleSelectClose={handleSelectClose}
                selectPopover={selectPopover}
                pharmacyMemberOptions={pharmacyMemberOptions}
                getAvailableProgramsForRow={getAvailableProgramsForRow}
                handleSelectValue={handleSelectValue}
                rows={rows}
                getMonitoringReasonForDrug={getMonitoringReasonForDrug}
                memberBadgeValue={memberBadgeValue}
                showMonitoringReason={showMonitoringReason}
                handleChange={handleChange}
                noteAnchor={noteAnchor}
                noteRowIdx={noteRowIdx}
                handleNoteClick={handleNoteClick}
                handleNoteClose={handleNoteClose}
                noteValue={noteValue}
                setNoteValue={setNoteValue}
                noteError={noteError}
                setNoteError={setNoteError}
                handleNoteSave={handleNoteSave}
                initialNoteValue={initialNoteValue}
                dynamicMethods={dynamicMethods}
                dynamicOutcomes={dynamicOutcomes}
                dynamicOutcomeReasons={dynamicOutcomeReasons}
                getDynamicOutcomeColor={getDynamicOutcomeColor}
              />
            ) : (
              <>
                <DesktopPendingOutcomesTable
                  pendingEntries={pendingEntries}
                  memberBadgeValue={memberBadgeValue}
                  showMonitoringReason={showMonitoringReason}
                  isOutreachContext={isOutreachContext}
                  handleRowClick={handleRowClick}
                  selectedRow={selectedRow}
                  title={title}
                  originalRowCount={originalRowCount}
                  selectPopover={selectPopover}
                  handleSelectClose={handleSelectClose}
                  handleSelectOpen={handleSelectOpen}
                  handleSelectValue={handleSelectValue}
                  getAvailableProgramsForRow={getAvailableProgramsForRow}
                  pharmacyMemberOptions={pharmacyMemberOptions}
                  getOutreachOptions={getOutreachOptions}
                  getMedicationOptionsForRow={getMedicationOptionsForRow}
                  getMonitoringReasonForDrug={getMonitoringReasonForDrug}
                  handleChange={handleChange}
                  sourceOpps={sourceOpps}
                  noteAnchor={noteAnchor}
                  noteRowIdx={noteRowIdx}
                  handleNoteClick={handleNoteClick}
                  handleNoteClose={handleNoteClose}
                  noteValue={noteValue}
                  setNoteValue={setNoteValue}
                  noteError={noteError}
                  setNoteError={setNoteError}
                  handleNoteSave={handleNoteSave}
                  initialNoteValue={initialNoteValue}
                  rows={rows}
                  dynamicMethods={dynamicMethods}
                  dynamicOutcomes={dynamicOutcomes}
                  dynamicOutcomeReasons={dynamicOutcomeReasons}
                  getDynamicOutcomeColor={getDynamicOutcomeColor}
                />
                <DesktopFinalizedOutcomesTable
                  finalEntries={finalEntries}
                  memberBadgeValue={memberBadgeValue}
                  showMonitoringReason={showMonitoringReason}
                  isOutreachContext={isOutreachContext}
                  handleRowClick={handleRowClick}
                  selectedRow={selectedRow}
                  title={title}
                  originalRowCount={originalRowCount}
                  selectPopover={selectPopover}
                  handleSelectClose={handleSelectClose}
                  handleSelectOpen={handleSelectOpen}
                  handleSelectValue={handleSelectValue}
                  getAvailableProgramsForRow={getAvailableProgramsForRow}
                  pharmacyMemberOptions={pharmacyMemberOptions}
                  getOutreachOptions={getOutreachOptions}
                  getMedicationOptionsForRow={getMedicationOptionsForRow}
                  getMonitoringReasonForDrug={getMonitoringReasonForDrug}
                  handleChange={handleChange}
                  sourceOpps={sourceOpps}
                  noteAnchor={noteAnchor}
                  noteRowIdx={noteRowIdx}
                  handleNoteClick={handleNoteClick}
                  handleNoteClose={handleNoteClose}
                  noteValue={noteValue}
                  setNoteValue={setNoteValue}
                  noteError={noteError}
                  setNoteError={setNoteError}
                  handleNoteSave={handleNoteSave}
                  initialNoteValue={initialNoteValue}
                  rows={rows}
                  dynamicMethods={dynamicMethods}
                  dynamicOutcomes={dynamicOutcomes}
                  dynamicOutcomeReasons={dynamicOutcomeReasons}
                  getDynamicOutcomeColor={getDynamicOutcomeColor}
                />
                <Box width="100%" pl={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={textingConsentObtained}
                        onChange={(e) =>
                          setTextingConsentObtained(e.target.checked)
                        }
                        name="textingConsentObtained"
                        sx={{
                          color: "#9F9DB6",
                          "&.Mui-checked": {
                            color: "#403C6F !important",
                          },
                        }}
                        disabled={!canWrite}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: "13px", color: "#403C6F" }}>
                        Verbal consent obtained for informational/service text
                        messages; patient advised texts are unsecure and PHI
                        will be limited.
                      </Typography>
                    }
                  />
                </Box>
                <HistoryNotePopover
                  historyNoteAnchor={historyNoteAnchor}
                  handleHistoryNoteClose={handleHistoryNoteClose}
                  isMobile={isMobile}
                  dynamicOutcomes={dynamicOutcomes}
                  dynamicMethods={dynamicMethods}
                  dynamicOutcomeReasons={dynamicOutcomeReasons}
                  getDynamicOutcomeColor={getDynamicOutcomeColor}
                  activeHistoryNote={activeHistoryNote}
                />
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "flex-end",
            gap: "10px",
            mt: 0.5,
            position: "relative",
            zIndex: 0,
          }}
        >
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              height: 38,
              gap: "10px",
              opacity: 1,
              borderRadius: "52px",
              borderWidth: "1px",
              color: "#403C6F",
              border: "1px solid #9F9DB6",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              !canWrite ||
              !hasChanges ||
              isSubmitting ||
              !checkRequiredFields() ||
              changedOutcomeIndices.length === 0
            }
            sx={{
              height: 38,
              gap: "10px",
              opacity: 1,
              borderRadius: "52px",
              borderWidth: "1px",
              backgroundColor: isSubmitting ? "#908f9bff" : "#3F3D56",
              cursor:
                !hasChanges ||
                isSubmitting ||
                !canWrite ||
                !checkRequiredFields() ||
                changedOutcomeIndices.length === 0
                  ? "not-allowed"
                  : "pointer",
              pointerEvents:
                !hasChanges ||
                isSubmitting ||
                !canWrite ||
                !checkRequiredFields() ||
                changedOutcomeIndices.length === 0
                  ? "none"
                  : "auto",
              "&:hover": {
                backgroundColor:
                  !hasChanges ||
                  isSubmitting ||
                  !canWrite ||
                  !checkRequiredFields() ||
                  changedOutcomeIndices.length === 0
                    ? "#908f9bff"
                    : "#2F2C46",
              },
              "&.Mui-disabled": {
                backgroundColor: "#EFEFEF",
                color: "#999893",
              },
            }}
            title={getSubmitButtonTooltip()}
          >
            {isSubmitting ? "Submitting disposition..." : "Submit Disposition"}
          </Button>
        </DialogActions>
        <ConfirmationDialog
          open={confirmationOpen}
          title="Confirm Disposition"
          content="Are you sure you want to submit this disposition?"
          onClose={() => setConfirmationOpen(false)}
          onConfirm={handleConfirmDisposition}
          icon={<img src={SaveIcon} alt="save" width={20} height={20} />}
          confirmText={"Confirm"}
          outcomeColors={dynamicOutcomeColors}
          message={(function () {
            const changed = getChangedIndices().filter(
              (idx) => !!rows[idx]?.outcome,
            );
            if (changed.length === 0) return "No changes to submit";
            return changed
              .map((rowIdx, seqIdx) => {
                const row = rows[rowIdx] || {};
                const outcomeText = row.outcome || "-";
                const reasonText = row.outcomeReason
                  ? `, Reason: ${row.outcomeReason}`
                  : "";
                if (isOutreachContext()) {
                  return `${seqIdx + 1}. Member: ${row.program || "-"}, Outcome: ${outcomeText}${reasonText}`;
                }
                return `${seqIdx + 1}. Program: ${row.program || "-"}, Status: ${row.status || "-"}, Outcome: ${outcomeText}${reasonText}`;
              })
              .join("\n");
          })()}
        />
      </Box>
    </Dialog>
  );
};

const dispositionEntryShape = PropTypes.shape({
  category: PropTypes.string,
  drug: PropTypes.string,
  drugNdc: PropTypes.string,
  status: PropTypes.string,
  monitoringReason: PropTypes.string,
  method: PropTypes.string,
  recentActivity: PropTypes.shape({
    status: PropTypes.string,
  }),
  programId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  drugId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
});

AddEditOutcomeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  title: PropTypes.string,
  dispositionOpportunity: PropTypes.oneOfType([
    // Case 1: Array of entries
    PropTypes.arrayOf(dispositionEntryShape),
    // Case 2: Object with selected/data arrays
    PropTypes.shape({
      selected: PropTypes.arrayOf(dispositionEntryShape),
      data: PropTypes.arrayOf(dispositionEntryShape),
      // Fallback fields if it's a single object acting as an entry
      category: PropTypes.string,
      drug: PropTypes.string,
      monitoringReason: PropTypes.string,
      status: PropTypes.string,
      method: PropTypes.string,
      recentActivity: PropTypes.shape({
        status: PropTypes.string,
      }),
    }),
  ]),
  programOptions: PropTypes.array,
  programDrugMap: PropTypes.object,
  memberId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setSnackbar: PropTypes.func,
  showMonitoringReason: PropTypes.bool,
  memberBadgeValue: PropTypes.string,
  canWrite: PropTypes.bool,
  outreachData: PropTypes.shape({
    outcomes: PropTypes.arrayOf(
      PropTypes.shape({
        displayName: PropTypes.string,
        colourCode: PropTypes.string,
        subReasons: PropTypes.arrayOf(
          PropTypes.shape({
            displayName: PropTypes.string,
          }),
        ),
      }),
    ),
    outreachMethods: PropTypes.arrayOf(
      PropTypes.shape({
        methodName: PropTypes.string,
      }),
    ),
  }),
  textingConsentObtained: PropTypes.bool,
};

export default AddEditOutcomeModal;
